import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Slider } from "../components/ui/slider";
import LivingGardenOverlay, { AchievementCelebration } from "../components/LivingGardenOverlay";
import RenderPaintEditor from "../components/RenderPaintEditor";
import GetStartedChecklist from "../components/GetStartedChecklist";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Upload, Trash2, Save, AlertTriangle, CheckCircle, Plus, 
  Loader2, ArrowLeft, Image, Leaf, RefreshCw, X, History, 
  Share2, Copy, Check, Twitter, Facebook, Palette, Sparkles,
  Building2, Armchair, Shapes, Grid3X3, Heart, Download,
  Eye, Brush, Wand2, Users, Globe, Play, Pause, MapPin, 
  Thermometer, Droplets, Sun, Info, ChevronRight, User, ZoomIn, ZoomOut,
  Paintbrush, Edit, Menu, PanelLeftClose, PanelLeft, Search, Navigation, Apple,
  Pencil, Eraser, ImagePlus, Sticker, Tag, Type, Mic, MicOff, Move, Grip, Map
} from "lucide-react";

// Fix for Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const SITE_URL = window.location.origin;

// Design Modes
const MODES = {
  DESIGN: "design",      // Standard design with plants & structures
  ZEN: "zen",            // Relaxing tap-to-color mode
  CREATIVE: "creative",  // Full color spectrum, textures, gradients
  GALLERY: "gallery",    // Browse & remix community designs
  TEMPLATES: "templates" // Browse world garden templates
};

const VisualDesignTool = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login } = useAuth();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Get URL params for filtering
  const continentFilter = searchParams.get('continent');
  
  // Mode state - start in templates if coming from aesthetic link
  const [mode, setMode] = useState(continentFilter ? MODES.TEMPLATES : MODES.DESIGN);
  
  // Core design state
  const [designs, setDesigns] = useState([]);
  const [currentDesign, setCurrentDesign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Canvas state
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [plants, setPlants] = useState([]);
  const [structures, setStructures] = useState([]);
  const [previousSeasonPlants, setPreviousSeasonPlants] = useState([]);
  const [canvasWidth, setCanvasWidth] = useState(20);
  const [canvasHeight, setCanvasHeight] = useState(20);
  const [designName, setDesignName] = useState("My Garden Design");
  
  // Interaction state
  const [selectedItem, setSelectedItem] = useState(null);
  const [draggingItem, setDraggingItem] = useState(null);
  const [activeTab, setActiveTab] = useState("plants");
  
  // Warnings
  const [spacingWarnings, setSpacingWarnings] = useState([]);
  const [rotationWarnings, setRotationWarnings] = useState([]);
  
  // Databases
  const [plantDatabase, setPlantDatabase] = useState([]);
  const [structuresDatabase, setStructuresDatabase] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Share state
  const [shareId, setShareId] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Zen mode state
  const [zenColors, setZenColors] = useState({});
  const [selectedPalette, setSelectedPalette] = useState("spring");
  const [zenPalettes, setZenPalettes] = useState({});
  const [selectedColor, setSelectedColor] = useState("#98FB98");
  
  // Creative mode state
  const [creativeColor, setCreativeColor] = useState("#4CAF50");
  const [brushSize, setBrushSize] = useState(20);
  const [creativeTexture, setCreativeTexture] = useState("solid");
  const [customLabels, setCustomLabels] = useState([]);
  
  // Emoji Drawing Mode state
  const [drawMode, setDrawMode] = useState(false);
  const [selectedDrawEmoji, setSelectedDrawEmoji] = useState({ emoji: "💜", name: "Lavender", spacing: 2 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawnPaths, setDrawnPaths] = useState([]); // Array of drawn emoji paths
  const [currentPath, setCurrentPath] = useState([]); // Current drawing path
  const [customStickers, setCustomStickers] = useState([]); // User uploaded stickers
  const [showStickerUpload, setShowStickerUpload] = useState(false);
  const [signStyle, setSignStyle] = useState("wooden"); // wooden, modern, chalkboard, emoji
  const [draggingSign, setDraggingSign] = useState(null); // For dragging sign positions
  const [isListening, setIsListening] = useState(false); // Voice input state
  
  // Gallery state
  const [galleryDesigns, setGalleryDesigns] = useState([]);
  const [galleryPage, setGalleryPage] = useState(1);
  const [gallerySort, setGallerySort] = useState("recent");
  
  // AI Render state
  const [renderStyle, setRenderStyle] = useState("realistic");
  const [rendering, setRendering] = useState(false);
  const [renderImage, setRenderImage] = useState(null);
  const [showPaintEditor, setShowPaintEditor] = useState(false);
  const [editedRenderImage, setEditedRenderImage] = useState(null);
  
  // Season toggle
  const [showPreviousSeason, setShowPreviousSeason] = useState(false);
  
  // Living Garden Animation state
  const [isGardenAlive, setIsGardenAlive] = useState(false);
  const [newAchievement, setNewAchievement] = useState(null);
  
  // World Templates state
  const [worldTemplates, setWorldTemplates] = useState([]);
  const [selectedContinent, setSelectedContinent] = useState(continentFilter);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Address-based template generation state
  const [addressSearch, setAddressSearch] = useState("");
  const [generatingZoneTemplate, setGeneratingZoneTemplate] = useState(false);
  const [generatedZoneTemplate, setGeneratedZoneTemplate] = useState(null);
  
  // Map state for location picker
  const [showLocationMap, setShowLocationMap] = useState(false);
  const [mapPosition, setMapPosition] = useState({ lat: 18.3358, lng: -64.8963 }); // USVI default
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapSatelliteView, setMapSatelliteView] = useState(false);

  useEffect(() => {
    loadPlantDatabase();
    loadStructuresDatabase();
    loadZenPalettes();
    if (user) {
      loadDesigns();
    } else {
      setLoading(false);
    }
  }, [user]);
  
  useEffect(() => {
    if (plants.length > 1) checkWarnings();
  }, [plants]);
  
  useEffect(() => {
    if (mode === MODES.GALLERY) loadGalleryDesigns();
    if (mode === MODES.TEMPLATES) loadWorldTemplates(selectedContinent);
  }, [mode, gallerySort, galleryPage, selectedContinent]);

  const loadPlantDatabase = async () => {
    try {
      const response = await axios.get(`${API}/plants/database`);
      setPlantDatabase(response.data);
    } catch (error) {
      console.error("Failed to load plants:", error);
    }
  };
  
  const loadStructuresDatabase = async () => {
    try {
      const response = await axios.get(`${API}/structures`);
      setStructuresDatabase(response.data.structures || []);
    } catch (error) {
      console.error("Failed to load structures:", error);
    }
  };
  
  const loadZenPalettes = async () => {
    try {
      const response = await axios.get(`${API}/zen/palettes`);
      setZenPalettes(response.data.palettes || {});
    } catch (error) {
      console.error("Failed to load palettes:", error);
    }
  };
  
  const loadDesigns = async () => {
    try {
      const response = await axios.get(`${API}/garden-designs`, { withCredentials: true });
      setDesigns(response.data);
    } catch (error) {
      console.error("Failed to load designs:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadGalleryDesigns = async () => {
    try {
      const response = await axios.get(`${API}/gallery?page=${galleryPage}&sort=${gallerySort}`);
      setGalleryDesigns(response.data.designs || []);
    } catch (error) {
      console.error("Failed to load gallery:", error);
    }
  };
  
  const loadWorldTemplates = async (continent = null) => {
    setLoadingTemplates(true);
    try {
      const url = continent ? `${API}/templates?continent=${continent}` : `${API}/templates`;
      const response = await axios.get(url);
      setWorldTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to load templates:", error);
      toast.error("Failed to load garden templates");
    } finally {
      setLoadingTemplates(false);
    }
  };
  
  const viewTemplateDetails = async (templateId) => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}`);
      setSelectedTemplate(response.data);
    } catch (error) {
      toast.error("Failed to load template details");
    }
  };
  
  const applyTemplate = async (templateId) => {
    if (!user) {
      toast.error("Please log in to use templates");
      login();
      return;
    }
    
    try {
      const response = await axios.post(`${API}/templates/${templateId}/use`, {}, { withCredentials: true });
      setCurrentDesign(response.data);
      setDesignName(response.data.name);
      setPlants(response.data.plants || []);
      setStructures(response.data.structures || []);
      setPreviousSeasonPlants([]);
      setBackgroundImage(null);
      setZenColors({});
      setCustomLabels([]);
      setCanvasWidth(response.data.canvas_width_feet || 20);
      setCanvasHeight(response.data.canvas_height_feet || 20);
      setMode(MODES.DESIGN);
      setSelectedTemplate(null);
      toast.success(`Started with ${response.data.name}!`);
      loadDesigns();
    } catch (error) {
      toast.error("Failed to use template");
    }
  };

  // Generate template based on address/zone
  const generateZoneTemplate = async () => {
    if (!addressSearch.trim()) {
      toast.error("Please enter an address");
      return;
    }
    
    setGeneratingZoneTemplate(true);
    try {
      const response = await axios.post(`${API}/templates/generate-from-address`, {
        address: addressSearch.trim()
      });
      
      setGeneratedZoneTemplate(response.data);
      toast.success(`Found zone ${response.data.hardiness_zone} for your location!`);
    } catch (error) {
      console.error("Failed to generate template:", error);
      toast.error(error.response?.data?.detail || "Failed to generate template for this address");
    } finally {
      setGeneratingZoneTemplate(false);
    }
  };

  // Generate template from map coordinates
  const generateFromMapLocation = async (lat, lng) => {
    setGeneratingZoneTemplate(true);
    setSelectedLocation({ lat, lng });
    try {
      // Reverse geocode to get address
      const geoResponse = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const geoData = await geoResponse.json();
      const address = geoData.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      
      setAddressSearch(address);
      
      const response = await axios.post(`${API}/templates/generate-from-address`, {
        address: address,
        latitude: lat,
        longitude: lng
      });
      
      setGeneratedZoneTemplate(response.data);
      setShowLocationMap(false);
      toast.success(`Found zone ${response.data.hardiness_zone} for your location!`);
    } catch (error) {
      console.error("Failed to generate template:", error);
      toast.error(error.response?.data?.detail || "Failed to generate template for this location");
    } finally {
      setGeneratingZoneTemplate(false);
    }
  };

  // Map click handler component
  const MapClickHandler = () => {
    useMapEvents({
      click: (e) => {
        generateFromMapLocation(e.latlng.lat, e.latlng.lng);
      }
    });
    return null;
  };

  // Get user's current location
  const useCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setMapPosition({ lat: latitude, lng: longitude });
          setSelectedLocation({ lat: latitude, lng: longitude });
          toast.success("Found your location!");
        },
        (error) => {
          toast.error("Could not get your location. Please enable location services.");
        }
      );
    } else {
      toast.error("Geolocation is not supported by your browser");
    }
  };

  const useGeneratedTemplate = async () => {
    if (!generatedZoneTemplate) return;
    
    // For guests, create a local design with the template plants
    if (!user) {
      const guestDesign = {
        id: `guest-${Date.now()}`,
        name: generatedZoneTemplate.name || "My Zone Garden",
        canvas_width_feet: 20,
        canvas_height_feet: 20,
        plants: generatedZoneTemplate.plants || [],
        structures: [],
        previous_season_plants: []
      };
      setCurrentDesign(guestDesign);
      setDesignName(guestDesign.name);
      setPlants(generatedZoneTemplate.plants || []);
      setStructures([]);
      setPreviousSeasonPlants([]);
      setBackgroundImage(null);
      setZenColors({});
      setCustomLabels([]);
      setMode(MODES.DESIGN);
      setGeneratedZoneTemplate(null);
      setAddressSearch("");
      toast.success("Template applied! Sign in to save your work.");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/garden-designs`, {
        name: generatedZoneTemplate.name || "My Zone Garden",
        canvas_width_feet: 20,
        canvas_height_feet: 20,
        plants: generatedZoneTemplate.plants || [],
        structures: [],
        previous_season_plants: [],
        zone_info: {
          zone: generatedZoneTemplate.hardiness_zone,
          address: addressSearch,
          climate: generatedZoneTemplate.climate_zone
        }
      }, { withCredentials: true });
      
      setCurrentDesign(response.data);
      setDesignName(response.data.name);
      setPlants(generatedZoneTemplate.plants || []);
      setStructures([]);
      setPreviousSeasonPlants([]);
      setBackgroundImage(null);
      setZenColors({});
      setCustomLabels([]);
      setMode(MODES.DESIGN);
      setGeneratedZoneTemplate(null);
      setAddressSearch("");
      loadDesigns();
      toast.success(`Started your Zone ${generatedZoneTemplate.hardiness_zone} garden!`);
    } catch (error) {
      toast.error("Failed to create design from template");
    }
  };

  const createNewDesign = async () => {
    // For guests, create a local design
    if (!user) {
      const guestDesign = {
        id: `guest-${Date.now()}`,
        name: "My Garden Design",
        canvas_width_feet: 20,
        canvas_height_feet: 20,
        plants: [],
        structures: [],
        previous_season_plants: []
      };
      setCurrentDesign(guestDesign);
      setDesignName(guestDesign.name);
      setPlants([]);
      setStructures([]);
      setPreviousSeasonPlants([]);
      setBackgroundImage(null);
      setZenColors({});
      setCustomLabels([]);
      setMode(MODES.DESIGN);
      toast.success("Design started! Sign in to save your work.");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/garden-designs`, {
        name: "New Garden Design",
        canvas_width_feet: 20,
        canvas_height_feet: 20,
        plants: [],
        structures: [],
        previous_season_plants: []
      }, { withCredentials: true });
      
      setCurrentDesign(response.data);
      setDesignName(response.data.name);
      setPlants([]);
      setStructures([]);
      setPreviousSeasonPlants([]);
      setBackgroundImage(null);
      setZenColors({});
      setCustomLabels([]);
      toast.success("New design created");
      loadDesigns();
    } catch (error) {
      toast.error("Failed to create design");
    }
  };
  
  const loadDesign = async (designId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/garden-designs/${designId}`, { withCredentials: true });
      const design = response.data;
      
      setCurrentDesign(design);
      setDesignName(design.name);
      setPlants(design.plants || []);
      setStructures(design.structures || []);
      setPreviousSeasonPlants(design.previous_season_plants || []);
      setBackgroundImage(design.background_image);
      setCanvasWidth(design.canvas_width_feet || 20);
      setCanvasHeight(design.canvas_height_feet || 20);
      setShareId(design.share_id || null);
      setZenColors(design.zen_colors || {});
      setCustomLabels(design.custom_labels || []);
    } catch (error) {
      toast.error("Failed to load design");
    } finally {
      setLoading(false);
    }
  };
  
  const saveDesign = async () => {
    // Check for guest mode
    if (!user) {
      setShowSavePrompt(true);
      return;
    }
    
    if (!currentDesign) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/garden-designs/${currentDesign.id}`, {
        name: designName,
        background_image: backgroundImage,
        canvas_width_feet: canvasWidth,
        canvas_height_feet: canvasHeight,
        plants,
        structures,
        previous_season_plants: previousSeasonPlants,
        zen_colors: zenColors,
        custom_labels: customLabels
      }, { withCredentials: true });
      
      toast.success("Design saved!");
      loadDesigns();
      
      // Check for new achievements after saving
      checkForAchievements();
    } catch (error) {
      toast.error("Failed to save design");
    } finally {
      setSaving(false);
    }
  };
  
  const checkForAchievements = async () => {
    if (!user) return;
    try {
      const response = await axios.post(`${API}/gamification/check-achievements`, {}, { withCredentials: true });
      if (response.data.new_achievements?.length > 0) {
        // Show achievement celebration for the first new achievement
        setNewAchievement(response.data.new_achievements[0]);
      }
    } catch (error) {
      console.error("Failed to check achievements:", error);
    }
  };

  const addPlantToCanvas = (plantData) => {
    const newPlant = {
      id: `plant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: "plant",
      plant_id: plantData.id,
      plant_name: plantData.name,
      x: 50, y: 50,
      spacing_inches: plantData.spacing_inches || 12,
      icon: plantData.emoji || getPlantEmoji(plantData.name) || getCategoryIcon(plantData.category),
      color: getCategoryColor(plantData.category),
      justPlaced: true, // For animation
      // Planting & Harvest timing data for hover tooltips
      days_to_harvest: plantData.days_to_harvest,
      planting_months: plantData.planting_months || [],
      harvest_months: plantData.harvest_months || [],
      sun_requirement: plantData.sun_requirement,
      water_needs: plantData.water_needs,
      usda_zones: plantData.usda_zones || [],
      care_tips: plantData.care_tips,
      growing_guide: plantData.growing_guide,
    };
    
    // Trigger placement animation
    setPlacementAnimation({ x: 50, y: 50 });
    setTimeout(() => setPlacementAnimation(null), 600);
    
    if (showPreviousSeason) {
      setPreviousSeasonPlants(prev => [...prev, newPlant]);
    } else {
      setPlants(prev => [...prev, newPlant]);
    }
    
    // Remove justPlaced flag after animation
    setTimeout(() => {
      const updateFn = showPreviousSeason ? setPreviousSeasonPlants : setPlants;
      updateFn(prev => prev.map(p => p.id === newPlant.id ? {...p, justPlaced: false} : p));
    }, 500);
  };
  
  const addStructureToCanvas = (structureData) => {
    const newStructure = {
      id: `struct-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: "structure",
      struct_id: structureData.id,
      name: structureData.name,
      category: structureData.category,
      x: 50, y: 50,
      width: structureData.width,
      height: structureData.height,
      icon: structureData.icon,
      color: structureData.color,
    };
    setStructures(prev => [...prev, newStructure]);
  };

  const getCategoryIcon = (category) => {
    const icons = { vegetable: "🥬", herb: "🌿", fruit: "🍎", flower: "🌸", tree: "🌳" };
    return icons[category] || "🌱";
  };

  // Map plant names to specific emojis
  const getPlantEmoji = (plantName) => {
    const plantEmojis = {
      // Vegetables
      "Tomato": "🍅", "Tomatoes": "🍅", "Cherry Tomatoes": "🍅",
      "Carrot": "🥕", "Carrots": "🥕",
      "Pepper": "🫑", "Peppers": "🫑", "Bell Pepper": "🫑", "Bell Peppers": "🫑",
      "Hot Pepper": "🌶️", "Hot Peppers": "🌶️", "Scotch Bonnet": "🌶️", "Habanero": "🌶️", "Cayenne": "🌶️",
      "Lettuce": "🥬", "Kale": "🥬", "Spinach": "🥬", "Callaloo": "🥬", "Collard Greens": "🥬",
      "Corn": "🌽", "Sweet Corn": "🌽",
      "Cucumber": "🥒", "Cucumbers": "🥒",
      "Eggplant": "🍆", "Aubergine": "🍆",
      "Broccoli": "🥦",
      "Onion": "🧅", "Onions": "🧅",
      "Garlic": "🧄",
      "Potato": "🥔", "Potatoes": "🥔", "Sweet Potato": "🍠", "Yam": "🍠",
      "Pumpkin": "🎃", "Squash": "🎃", "Butternut Squash": "🎃",
      "Beans": "🫘", "Green Beans": "🫛", "Pigeon Peas": "🫛",
      "Peas": "🫛",
      "Avocado": "🥑", "Avocados": "🥑",
      "Ginger": "🫚", "Turmeric": "🫚",
      
      // Tropical Fruits
      "Strawberry": "🍓", "Strawberries": "🍓",
      "Blueberry": "🫐", "Blueberries": "🫐", "Acai": "🫐", "Açaí": "🫐",
      "Mango": "🥭", "Mangoes": "🥭", "Mangos": "🥭",
      "Banana": "🍌", "Bananas": "🍌", "Plantain": "🍌", "Apple Banana": "🍌",
      "Papaya": "🍑", "Papayas": "🍑", "Pawpaw": "🍑",
      "Watermelon": "🍉",
      "Cantaloupe": "🍈", "Melon": "🍈",
      "Grape": "🍇", "Grapes": "🍇",
      "Orange": "🍊", "Oranges": "🍊", "Citrus": "🍊", "Tangerine": "🍊", "Mandarin": "🍊",
      "Lemon": "🍋", "Lemons": "🍋", "Lime": "🍋", "Key Lime": "🍋",
      "Apple": "🍎", "Apples": "🍎",
      "Pear": "🍐", "Pears": "🍐",
      "Peach": "🍑", "Peaches": "🍑",
      "Cherry": "🍒", "Cherries": "🍒",
      "Pineapple": "🍍", "Pineapples": "🍍",
      "Coconut": "🥥", "Coconuts": "🥥", "Coconut Palm": "🥥",
      "Passion Fruit": "🫐", "Passionfruit": "🫐",
      "Guava": "🍐", "Guavas": "🍐",
      "Starfruit": "⭐", "Carambola": "⭐",
      "Dragon Fruit": "🐉", "Dragonfruit": "🐉", "Pitaya": "🐉",
      "Jackfruit": "🍈",
      "Soursop": "💚", "Graviola": "💚",
      "Breadfruit": "🍞",
      "Kiwi": "🥝", "Kiwifruit": "🥝",
      "Fig": "🟤", "Figs": "🟤",
      "Pomegranate": "🔴",
      "Lychee": "🩷", "Longan": "🩷", "Rambutan": "🩷",
      "Durian": "💛",
      "Tamarind": "🤎",
      
      // Herbs
      "Basil": "🌿", "Sweet Basil": "🌿", "Thai Basil": "🌿",
      "Mint": "🌿", "Peppermint": "🌿", "Spearmint": "🌿",
      "Oregano": "🌿",
      "Thyme": "🌿",
      "Rosemary": "🌿",
      "Parsley": "🌿",
      "Cilantro": "🌿", "Coriander": "🌿", "Culantro": "🌿",
      "Dill": "🌿",
      "Sage": "🌿",
      "Chives": "🌿",
      "Lemongrass": "🌿",
      "Bay Leaf": "🌿", "Bay Leaves": "🌿",
      "Curry Leaf": "🌿",
      "Epazote": "🌿",
      "Shiso": "🌿", "Perilla": "🌿",
      
      // Flowers
      "Sunflower": "🌻", "Sunflowers": "🌻",
      "Rose": "🌹", "Roses": "🌹",
      "Tulip": "🌷", "Tulips": "🌷",
      "Hibiscus": "🌺",
      "Lavender": "💜",
      "Marigold": "🌼", "Marigolds": "🌼",
      "Lantana": "🌸",
      "Orchid": "🌸",
      "Bougainvillea": "🌺",
      "Jasmine": "🤍",
      "Chamomile": "🌼",
      
      // Trees & Other
      "Moringa": "🌳",
      "Cassava": "🌱", "Yuca": "🌱",
      "Aloe Vera": "🌵", "Aloe": "🌵",
      "Taro": "🟣", "Dasheen": "🟣",
      "Bok Choy": "🥬", "Pak Choi": "🥬",
      "Napa Cabbage": "🥬", "Chinese Cabbage": "🥬",
      "Daikon": "🥕", "Radish": "🥕",
      "Edamame": "🫛", "Soybean": "🫛",
      "Bamboo": "🎋", "Bamboo Shoots": "🎋",
      "Wasabi": "💚",
      "Lotus": "🪷", "Lotus Root": "🪷",
      "Water Chestnut": "🤎",
      "Okra": "💚",
      "Artichoke": "💚",
      "Asparagus": "💚",
      "Leek": "🧅",
      "Fennel": "🌿",
      "Celery": "🥬",
      "Rhubarb": "🩷",
      "Beet": "🟣", "Beetroot": "🟣",
      "Turnip": "🤍",
      "Parsnip": "🤍",
      "Rutabaga": "🟡",
      "Kohlrabi": "💜",
      "Brussels Sprouts": "💚",
      "Cauliflower": "🤍",
      "Cabbage": "🥬",
      "Zucchini": "🥒", "Courgette": "🥒",
      "Bitter Melon": "💚", "Bitter Gourd": "💚",
      "Chayote": "💚",
      "Ackee": "🟡",
      "Cacao": "🤎", "Cocoa": "🤎",
      "Coffee": "☕", "Coffee Plant": "☕",
      "Tea": "🍵", "Tea Plant": "🍵",
      "Vanilla": "🤍",
      "Sugarcane": "🌾",
      "Sorghum": "🌾",
      "Millet": "🌾",
      "Quinoa": "🌾",
      "Amaranth": "🌾",
      "Sesame": "🤍",
      "Sunflower Seeds": "🌻",
      "Pumpkin Seeds": "🎃",
      "Flax": "🔵", "Flaxseed": "🔵",
      "Chia": "⚫",
      "Hemp": "💚",
    };
    
    // Try exact match first
    if (plantEmojis[plantName]) return plantEmojis[plantName];
    
    // Try partial match
    for (const [name, emoji] of Object.entries(plantEmojis)) {
      if (plantName?.toLowerCase().includes(name.toLowerCase())) return emoji;
    }
    
    return null;
  };
  
  const getCategoryColor = (category) => {
    const colors = { vegetable: "#4CAF50", herb: "#8BC34A", fruit: "#FF9800", flower: "#E91E63", tree: "#795548" };
    return colors[category] || "#4CAF50";
  };

  const removeItem = (itemId, itemType) => {
    if (itemType === "plant") {
      if (showPreviousSeason) {
        setPreviousSeasonPlants(prev => prev.filter(p => p.id !== itemId));
      } else {
        setPlants(prev => prev.filter(p => p.id !== itemId));
      }
    } else {
      setStructures(prev => prev.filter(s => s.id !== itemId));
    }
    setSelectedItem(null);
  };

  const handleCanvasClick = (e) => {
    if (mode === MODES.ZEN) {
      // Zen mode: Color the clicked element
      const itemId = e.target.dataset.itemId;
      if (itemId) {
        setZenColors(prev => ({ ...prev, [itemId]: selectedColor }));
      }
      return;
    }
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    // Find clicked item
    const allItems = [...(showPreviousSeason ? previousSeasonPlants : plants), ...structures];
    const clicked = allItems.find(item => {
      const distance = Math.sqrt((x - item.x) ** 2 + (y - item.y) ** 2);
      return distance < 5;
    });
    
    setSelectedItem(clicked?.id === selectedItem ? null : clicked?.id);
  };
  
  const handleCanvasMouseMove = (e) => {
    if (!draggingItem) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    
    // Update position based on item type
    const updatePlants = showPreviousSeason ? setPreviousSeasonPlants : setPlants;
    updatePlants(prev => prev.map(p => p.id === draggingItem ? { ...p, x, y } : p));
    setStructures(prev => prev.map(s => s.id === draggingItem ? { ...s, x, y } : s));
  };
  
  // Touch event handler for mobile drag
  const handleCanvasTouchMove = (e) => {
    if (!draggingItem || !e.touches[0]) return;
    e.preventDefault();
    const touch = e.touches[0];
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((touch.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((touch.clientY - rect.top) / rect.height) * 100));
    
    const updatePlants = showPreviousSeason ? setPreviousSeasonPlants : setPlants;
    updatePlants(prev => prev.map(p => p.id === draggingItem ? { ...p, x, y } : p));
    setStructures(prev => prev.map(s => s.id === draggingItem ? { ...s, x, y } : s));
  };

  // Emoji Drawing Mode Handlers
  const handleDrawStart = (e) => {
    if (!drawMode || !currentDesign) return;
    e.preventDefault();
    setIsDrawing(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    setCurrentPath([{ x, y, emoji: selectedDrawEmoji.emoji, name: selectedDrawEmoji.name }]);
  };

  const handleDrawMove = (e) => {
    if (!isDrawing || !drawMode) return;
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    
    // Add point if far enough from last point (spacing control)
    const lastPoint = currentPath[currentPath.length - 1];
    if (lastPoint) {
      const distance = Math.sqrt((x - lastPoint.x) ** 2 + (y - lastPoint.y) ** 2);
      const spacingThreshold = selectedDrawEmoji.spacing || 3; // % of canvas
      if (distance >= spacingThreshold) {
        setCurrentPath(prev => [...prev, { x, y, emoji: selectedDrawEmoji.emoji, name: selectedDrawEmoji.name }]);
      }
    }
  };

  const handleDrawEnd = () => {
    if (!isDrawing || currentPath.length === 0) {
      setIsDrawing(false);
      return;
    }
    
    // Count the number of plants/emojis placed
    const plantCount = currentPath.length;
    
    // Calculate the path length in feet
    let pathLengthFeet = 0;
    for (let i = 1; i < currentPath.length; i++) {
      const dx = (currentPath[i].x - currentPath[i-1].x) / 100 * canvasWidth;
      const dy = (currentPath[i].y - currentPath[i-1].y) / 100 * canvasHeight;
      pathLengthFeet += Math.sqrt(dx * dx + dy * dy);
    }
    
    // Save the completed path with metadata
    const completedPath = {
      id: `path-${Date.now()}`,
      points: currentPath,
      emoji: selectedDrawEmoji.emoji,
      name: selectedDrawEmoji.name,
      isCustom: selectedDrawEmoji.isCustom || false,
      customImage: selectedDrawEmoji.customImage || null,
      plantCount: plantCount,
      lengthFeet: Math.round(pathLengthFeet * 10) / 10,
      createdAt: new Date().toISOString(),
      label: {
        text: selectedDrawEmoji.name,
        show: true,
        position: { 
          x: currentPath[0].x, 
          y: Math.max(5, currentPath[0].y - 8) // Position above start of path
        }
      }
    };
    
    setDrawnPaths(prev => [...prev, completedPath]);
    setCurrentPath([]);
    setIsDrawing(false);
    
    // Show helpful message with plant count
    const lengthDisplay = pathLengthFeet >= 1 ? `(${Math.round(pathLengthFeet)} ft)` : '';
    toast.success(`Added ${plantCount} ${selectedDrawEmoji.name} ${lengthDisplay}`);
  };

  const deleteDrawnPath = (pathId) => {
    setDrawnPaths(prev => prev.filter(p => p.id !== pathId));
  };

  const clearAllPaths = () => {
    setDrawnPaths([]);
    toast.success("Cleared all drawn paths");
  };

  const updatePathLabel = (pathId, newLabel) => {
    setDrawnPaths(prev => prev.map(p => 
      p.id === pathId ? { ...p, label: { ...p.label, text: newLabel } } : p
    ));
  };

  const togglePathLabel = (pathId) => {
    setDrawnPaths(prev => prev.map(p => 
      p.id === pathId ? { ...p, label: { ...p.label, show: !p.label?.show } } : p
    ));
  };

  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editingLabelText, setEditingLabelText] = useState("");

  // Custom sticker upload handler
  const handleStickerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const newSticker = {
        id: `sticker-${Date.now()}`,
        name: file.name.replace(/\.[^/.]+$/, ""),
        image: event.target.result,
        isCustom: true
      };
      setCustomStickers(prev => [...prev, newSticker]);
      setSelectedDrawEmoji({ 
        emoji: "🖼️", 
        name: newSticker.name, 
        isCustom: true, 
        customImage: newSticker.image,
        spacing: 4 
      });
      setShowStickerUpload(false);
      toast.success(`Added custom sticker: ${newSticker.name}`);
    };
    reader.readAsDataURL(file);
  };

  const deleteCustomSticker = (stickerId) => {
    setCustomStickers(prev => prev.filter(s => s.id !== stickerId));
  };

  // Voice input for labels
  const startVoiceInput = (pathId) => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error("Voice input not supported in this browser");
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    
    setIsListening(true);
    setEditingLabelId(pathId);
    
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setEditingLabelText(transcript);
      updatePathLabel(pathId, transcript);
      setIsListening(false);
      setEditingLabelId(null);
      toast.success(`Label set to "${transcript}"`);
    };
    
    recognition.onerror = (event) => {
      setIsListening(false);
      toast.error("Voice recognition failed. Try again.");
    };
    
    recognition.onend = () => {
      setIsListening(false);
    };
    
    recognition.start();
    toast("🎤 Listening... speak the label name");
  };

  // Sign drag handlers
  const handleSignDragStart = (e, pathId) => {
    e.stopPropagation();
    setDraggingSign(pathId);
  };

  const handleSignDrag = (e) => {
    if (!draggingSign || !canvasRef.current) return;
    e.preventDefault();
    
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = Math.max(5, Math.min(95, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.max(5, Math.min(95, ((clientY - rect.top) / rect.height) * 100));
    
    setDrawnPaths(prev => prev.map(p => 
      p.id === draggingSign 
        ? { ...p, label: { ...p.label, position: { x, y } } } 
        : p
    ));
  };

  const handleSignDragEnd = () => {
    setDraggingSign(null);
  };

  // Sign style options
  const signStyles = [
    { id: "wooden", name: "Wooden", emoji: "🪵" },
    { id: "modern", name: "Modern", emoji: "🏷️" },
    { id: "chalkboard", name: "Chalkboard", emoji: "📝" },
    { id: "emoji", name: "Emoji Sign", emoji: "🪧" },
  ];

  // Default emoji options for drawing - includes permaculture fixtures
  const drawingEmojis = [
    // Flowers
    { emoji: "💜", name: "Lavender", spacing: 2, category: "flowers" },
    { emoji: "🌸", name: "Cherry Blossom", spacing: 2, category: "flowers" },
    { emoji: "🌺", name: "Hibiscus", spacing: 3, category: "flowers" },
    { emoji: "🌻", name: "Sunflower", spacing: 4, category: "flowers" },
    { emoji: "🌹", name: "Rose", spacing: 2, category: "flowers" },
    { emoji: "🌷", name: "Tulip", spacing: 2, category: "flowers" },
    { emoji: "🌼", name: "Daisy", spacing: 2, category: "flowers" },
    { emoji: "🪻", name: "Hyacinth", spacing: 2, category: "flowers" },
    // Vegetables
    { emoji: "🥬", name: "Callaloo/Greens", spacing: 3, category: "vegetables" },
    { emoji: "🥕", name: "Carrot", spacing: 2, category: "vegetables" },
    { emoji: "🍅", name: "Tomato", spacing: 4, category: "vegetables" },
    { emoji: "🌽", name: "Corn", spacing: 3, category: "vegetables" },
    { emoji: "🫑", name: "Bell Pepper", spacing: 3, category: "vegetables" },
    { emoji: "🌶️", name: "Scotch Bonnet", spacing: 3, category: "vegetables" },
    { emoji: "🥒", name: "Cucumber", spacing: 4, category: "vegetables" },
    { emoji: "🍠", name: "Sweet Potato", spacing: 4, category: "vegetables" },
    { emoji: "🫚", name: "Ginger", spacing: 3, category: "vegetables" },
    { emoji: "🥑", name: "Avocado", spacing: 6, category: "vegetables" },
    { emoji: "🍆", name: "Eggplant", spacing: 4, category: "vegetables" },
    { emoji: "🥔", name: "Potato", spacing: 3, category: "vegetables" },
    { emoji: "🧅", name: "Onion", spacing: 2, category: "vegetables" },
    { emoji: "🧄", name: "Garlic", spacing: 2, category: "vegetables" },
    { emoji: "🥦", name: "Broccoli", spacing: 4, category: "vegetables" },
    { emoji: "🎃", name: "Pumpkin/Squash", spacing: 6, category: "vegetables" },
    { emoji: "🫘", name: "Beans", spacing: 2, category: "vegetables" },
    { emoji: "🫛", name: "Peas", spacing: 2, category: "vegetables" },
    { emoji: "🥜", name: "Peanuts", spacing: 3, category: "vegetables" },
    { emoji: "🌰", name: "Chestnuts", spacing: 4, category: "vegetables" },
    // Tropical Fruits
    { emoji: "🥭", name: "Mango", spacing: 8, category: "tropical" },
    { emoji: "🍍", name: "Pineapple", spacing: 4, category: "tropical" },
    { emoji: "🥥", name: "Coconut", spacing: 10, category: "tropical" },
    { emoji: "🍌", name: "Banana", spacing: 6, category: "tropical" },
    { emoji: "🍑", name: "Papaya/Peach", spacing: 6, category: "tropical" },
    { emoji: "🍊", name: "Citrus/Orange", spacing: 6, category: "tropical" },
    { emoji: "🫐", name: "Acai/Berries", spacing: 3, category: "tropical" },
    { emoji: "🍇", name: "Grapes", spacing: 4, category: "tropical" },
    { emoji: "🌴", name: "Palm Tree", spacing: 10, category: "tropical" },
    // Other Fruits
    { emoji: "🍓", name: "Strawberry", spacing: 2, category: "fruits" },
    { emoji: "🍉", name: "Watermelon", spacing: 6, category: "fruits" },
    { emoji: "🍈", name: "Melon", spacing: 6, category: "fruits" },
    { emoji: "🍎", name: "Apple", spacing: 5, category: "fruits" },
    { emoji: "🍐", name: "Pear", spacing: 5, category: "fruits" },
    { emoji: "🍋", name: "Lemon/Lime", spacing: 5, category: "fruits" },
    { emoji: "🍒", name: "Cherry", spacing: 3, category: "fruits" },
    { emoji: "🥝", name: "Kiwi", spacing: 4, category: "fruits" },
    { emoji: "🫒", name: "Olive", spacing: 4, category: "fruits" },
    // Plants & Trees
    { emoji: "🌿", name: "Herbs", spacing: 2, category: "plants" },
    { emoji: "🪴", name: "Potted Plant", spacing: 5, category: "plants" },
    { emoji: "🌲", name: "Evergreen", spacing: 6, category: "plants" },
    { emoji: "🌳", name: "Tree", spacing: 8, category: "plants" },
    { emoji: "🌱", name: "Seedling", spacing: 2, category: "plants" },
    { emoji: "🌵", name: "Cactus/Aloe", spacing: 4, category: "plants" },
    { emoji: "🍀", name: "Clover", spacing: 2, category: "plants" },
    { emoji: "🍃", name: "Leaves", spacing: 2, category: "plants" },
    { emoji: "🍄", name: "Mushroom", spacing: 2, category: "plants" },
    // Grains & Rice
    { emoji: "🌾", name: "Wheat/Grain", spacing: 2, category: "grains" },
    { emoji: "🍚", name: "Rice", spacing: 3, category: "grains" },
    // Materials
    { emoji: "🪨", name: "Rocks/Border", spacing: 2, category: "materials" },
    { emoji: "🪵", name: "Wood/Mulch", spacing: 3, category: "materials" },
    { emoji: "💧", name: "Water/Irrigation", spacing: 2, category: "materials" },
    { emoji: "☀️", name: "Full Sun Area", spacing: 5, category: "materials" },
    // Permaculture Fixtures
    { emoji: "🛢️", name: "Rain Barrel", spacing: 8, category: "permaculture" },
    { emoji: "🔋", name: "Solar Panel", spacing: 6, category: "permaculture" },
    { emoji: "🍯", name: "Bee Hive", spacing: 8, category: "permaculture" },
    { emoji: "🐝", name: "Pollinator Zone", spacing: 4, category: "permaculture" },
    { emoji: "🐟", name: "Aquaponics/Pond", spacing: 10, category: "permaculture" },
    { emoji: "🌊", name: "Water Feature", spacing: 8, category: "permaculture" },
    { emoji: "🏠", name: "Garden Shed", spacing: 12, category: "permaculture" },
    { emoji: "🪺", name: "Bird House", spacing: 6, category: "permaculture" },
    { emoji: "♻️", name: "Compost Bin", spacing: 8, category: "permaculture" },
    { emoji: "🌡️", name: "Greenhouse", spacing: 12, category: "permaculture" },
    { emoji: "🚿", name: "Drip Irrigation", spacing: 3, category: "permaculture" },
    { emoji: "🦋", name: "Butterfly Garden", spacing: 5, category: "permaculture" },
    { emoji: "🐓", name: "Chicken Coop", spacing: 10, category: "permaculture" },
    { emoji: "🐐", name: "Goat Area", spacing: 12, category: "permaculture" },
  ];

  const checkWarnings = async () => {
    if (plants.length < 2) return;
    try {
      const response = await axios.post(`${API}/garden-designs/check-spacing`, {
        plants, canvas_width_feet: canvasWidth, canvas_height_feet: canvasHeight
      });
      setSpacingWarnings(response.data.warnings || []);
    } catch (error) {
      console.error("Failed to check warnings:", error);
    }
  };

  const generateAIRender = async () => {
    if (!currentDesign) return;
    setRendering(true);
    try {
      // Include drawn paths data for the AI render
      const drawnPathsData = drawnPaths.map(path => ({
        name: path.name,
        lengthFeet: path.lengthFeet,
        pointCount: path.points.length,
        emoji: path.emoji
      }));
      
      const response = await axios.post(
        `${API}/garden-designs/${currentDesign.id}/render`,
        { 
          style: renderStyle, 
          include_labels: true,
          drawn_paths: drawnPathsData,
          canvas_width_feet: canvasWidth,
          canvas_height_feet: canvasHeight
        },
        { withCredentials: true }
      );
      setRenderImage(response.data.image_url);
      toast.success("Beautiful render generated!");
    } catch (error) {
      toast.error("Failed to generate render");
    } finally {
      setRendering(false);
    }
  };
  
  const submitToGallery = async () => {
    if (!currentDesign) return;
    try {
      await axios.post(`${API}/gallery/submit`, {
        design_id: currentDesign.id,
        tags: ["garden", designName.toLowerCase().includes("vegetable") ? "vegetable" : "mixed"]
      }, { withCredentials: true });
      toast.success("Design submitted to community gallery!");
    } catch (error) {
      toast.error("Failed to submit to gallery");
    }
  };
  
  const remixDesign = async (galleryId) => {
    try {
      const response = await axios.post(`${API}/gallery/${galleryId}/remix`, {}, { withCredentials: true });
      toast.success("Design remixed! Opening your copy...");
      loadDesign(response.data.design_id);
      setMode(MODES.DESIGN);
    } catch (error) {
      toast.error("Failed to remix design");
    }
  };
  
  const likeDesign = async (galleryId) => {
    try {
      const response = await axios.post(`${API}/gallery/${galleryId}/like`, {}, { withCredentials: true });
      toast.success(response.data.liked ? "Liked!" : "Unliked");
      loadGalleryDesigns();
    } catch (error) {
      toast.error("Failed to like design");
    }
  };

  const shareDesign = async () => {
    if (!currentDesign) return;
    try {
      const response = await axios.post(`${API}/garden-designs/${currentDesign.id}/share`, {}, { withCredentials: true });
      setShareId(response.data.share_id);
      setShowShareMenu(true);
      toast.success("Share link created!");
    } catch (error) {
      toast.error("Failed to create share link");
    }
  };
  
  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(`${SITE_URL}/shared/${shareId}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied!");
    } catch { toast.error("Failed to copy"); }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setBackgroundImage(event.target.result);
      toast.success("Background uploaded");
    };
    reader.readAsDataURL(file);
  };

  const filteredPlants = plantDatabase.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const filteredStructures = structuresDatabase.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const allCanvasItems = [...(showPreviousSeason ? previousSeasonPlants : plants), ...structures];
  const totalWarnings = spacingWarnings.length + rotationWarnings.length;
  const currentPalette = zenPalettes[selectedPalette] || [];

  // Guest mode - allow browsing templates and designing, but prompt to save
  const [isGuestMode, setIsGuestMode] = useState(!user);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [plantScale, setPlantScale] = useState(1.5); // Default larger plants
  const [placementAnimation, setPlacementAnimation] = useState(null);
  
  // Mobile responsiveness state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showMobileToolbar, setShowMobileToolbar] = useState(false);

  // Show save prompt for guests
  const handleGuestSave = () => {
    if (!user) {
      setShowSavePrompt(true);
      return false;
    }
    return true;
  };

  if (loading && user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-amber-50">
      {/* Save Prompt Modal for Guests */}
      <AnimatePresence>
        {showSavePrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowSavePrompt(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md w-full p-8 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Save Your Masterpiece!</h2>
              <p className="text-muted-foreground mb-6">
                Sign in to save your garden design, share it with the community, and track your progress.
              </p>
              <div className="space-y-3">
                <Button onClick={login} className="w-full" size="lg" data-testid="login-to-save-btn">
                  <User className="w-5 h-5 mr-2" /> Sign In to Save
                </Button>
                <Button variant="outline" onClick={() => setShowSavePrompt(false)} className="w-full">
                  Keep Designing
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b sticky top-0 z-40">
        <div className="max-w-full mx-auto px-2 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Mobile sidebar toggle */}
            {mode !== MODES.GALLERY && mode !== MODES.TEMPLATES && (
              <Button 
                variant="ghost" 
                size="sm"
                className="md:hidden"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="mobile-sidebar-toggle"
              >
                {sidebarOpen ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeft className="w-5 h-5" />}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="back-btn" className="hidden sm:flex">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn-mobile" className="sm:hidden">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-sm sm:text-lg font-bold flex items-center gap-1 sm:gap-2">
              <Leaf className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              <span className="hidden xs:inline">Design Studio</span>
              <span className="xs:hidden">Studio</span>
            </h1>
          </div>
          
          {/* Mode Switcher - scrollable on mobile */}
          <div className="flex items-center gap-1 sm:gap-2 bg-gray-100 rounded-lg p-0.5 sm:p-1 overflow-x-auto flex-shrink-0 max-w-[45%] sm:max-w-none">
            {[
              { id: MODES.TEMPLATES, icon: Globe, label: "Explore" },
              { id: MODES.DESIGN, icon: Grid3X3, label: "Design" },
              { id: MODES.ZEN, icon: Palette, label: "Zen" },
              { id: MODES.CREATIVE, icon: Brush, label: "Creative" },
              { id: MODES.GALLERY, icon: Users, label: "Gallery" },
            ].map(m => (
              <Button
                key={m.id}
                variant={mode === m.id ? "default" : "ghost"}
                size="sm"
                onClick={() => { setMode(m.id); setSidebarOpen(false); }}
                className="gap-1 px-2 sm:px-3 flex-shrink-0"
                data-testid={`mode-${m.id}`}
              >
                <m.icon className="w-4 h-4" />
                <span className="hidden md:inline">{m.label}</span>
              </Button>
            ))}
          </div>
          
          {currentDesign && mode !== MODES.GALLERY && (
            <div className="flex items-center gap-1 sm:gap-2">
              <Input
                value={designName}
                onChange={(e) => setDesignName(e.target.value)}
                className="w-20 sm:w-40 text-sm"
                data-testid="design-name-input"
              />
              <Button onClick={saveDesign} disabled={saving} data-testid="save-btn" size="sm">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              </Button>
              <Button variant="outline" size="sm" className="hidden sm:flex" onClick={shareId ? () => setShowShareMenu(!showShareMenu) : shareDesign}>
                <Share2 className="w-4 h-4" />
              </Button>
              
              {showShareMenu && shareId && (
                <div className="absolute right-4 top-16 bg-white rounded-lg shadow-lg border p-4 z-50 w-64">
                  <p className="text-sm font-medium mb-3">Share your design</p>
                  <div className="flex gap-2 mb-2">
                    <Button size="sm" variant="outline" onClick={copyShareLink}>
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => window.open(`https://twitter.com/intent/tweet?text=Check out my garden!&url=${SITE_URL}/shared/${shareId}`, "_blank")}>
                      <Twitter className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => window.open(`https://facebook.com/sharer/sharer.php?u=${SITE_URL}/shared/${shareId}`, "_blank")}>
                      <Facebook className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button size="sm" className="w-full" onClick={submitToGallery}>
                    <Globe className="w-4 h-4 mr-1" /> Submit to Gallery
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      <div className="flex h-[calc(100vh-57px)] relative">
        {/* Mobile Sidebar Overlay */}
        <AnimatePresence>
          {sidebarOpen && mode !== MODES.GALLERY && mode !== MODES.TEMPLATES && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-30 md:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}
        </AnimatePresence>
        
        {/* Left Panel - Responsive Sidebar */}
        {mode !== MODES.GALLERY && mode !== MODES.TEMPLATES && (
          <aside className={`
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            md:translate-x-0
            fixed md:relative
            left-0 top-[57px] md:top-0
            h-[calc(100vh-57px)]
            w-72 sm:w-64 md:w-72
            bg-white border-r 
            overflow-hidden flex flex-col
            z-40 md:z-auto
            transition-transform duration-300 ease-in-out
            shadow-lg md:shadow-none
          `}>
            {mode === MODES.DESIGN && (
              <>
                <div className="p-4 border-b">
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="search-input"
                  />
                </div>
                
                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
                  <TabsList className="w-full grid grid-cols-2 m-2">
                    <TabsTrigger value="plants"><Leaf className="w-4 h-4 mr-1" />Plants</TabsTrigger>
                    <TabsTrigger value="structures"><Building2 className="w-4 h-4 mr-1" />Structures</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="plants" className="flex-1 overflow-y-auto p-2 space-y-1">
                    {!currentDesign ? (
                      <p className="text-center py-8 text-muted-foreground">Select or create a design first</p>
                    ) : (
                      filteredPlants.slice(0, 50).map(plant => (
                        <button
                          key={plant.id}
                          onClick={() => addPlantToCanvas(plant)}
                          className="w-full p-2 text-left rounded-lg hover:bg-green-50 flex items-center gap-2"
                          data-testid={`add-plant-${plant.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <span className="text-xl">{getCategoryIcon(plant.category)}</span>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{plant.name}</p>
                            <p className="text-xs text-muted-foreground">{plant.spacing_inches}&quot; spacing</p>
                          </div>
                          <Plus className="w-4 h-4 text-muted-foreground" />
                        </button>
                      ))
                    )}
                  </TabsContent>
                  
                  <TabsContent value="structures" className="flex-1 overflow-y-auto p-2 space-y-1">
                    {!currentDesign ? (
                      <p className="text-center py-8 text-muted-foreground">Select or create a design first</p>
                    ) : (
                      <>
                        {["building", "furniture", "feature", "shape"].map(cat => (
                          <div key={cat} className="mb-4">
                            <p className="text-xs font-semibold uppercase text-muted-foreground mb-2 px-2">{cat}s</p>
                            {filteredStructures.filter(s => s.category === cat).map(struct => (
                              <button
                                key={struct.id}
                                onClick={() => addStructureToCanvas(struct)}
                                className="w-full p-2 text-left rounded-lg hover:bg-amber-50 flex items-center gap-2"
                              >
                                <span className="text-xl">{struct.icon}</span>
                                <span className="font-medium text-sm">{struct.name}</span>
                                <Plus className="w-4 h-4 ml-auto text-muted-foreground" />
                              </button>
                            ))}
                          </div>
                        ))}
                      </>
                    )}
                  </TabsContent>
                </Tabs>
              </>
            )}
            
            {mode === MODES.ZEN && (
              <div className="p-4 space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Palette className="w-5 h-5" /> Zen Coloring
                </h3>
                <p className="text-sm text-muted-foreground">Tap any element to color it. Relax and create.</p>
                
                <div className="space-y-2">
                  <Label>Color Palette</Label>
                  <select 
                    value={selectedPalette}
                    onChange={(e) => setSelectedPalette(e.target.value)}
                    className="w-full p-2 border rounded-lg"
                  >
                    {Object.keys(zenPalettes).map(p => (
                      <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {currentPalette.map((color, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedColor(color)}
                      className={`w-10 h-10 rounded-full border-2 transition-all ${selectedColor === color ? 'border-black scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                
                <div className="space-y-2">
                  <Label>Custom Color</Label>
                  <input
                    type="color"
                    value={selectedColor}
                    onChange={(e) => setSelectedColor(e.target.value)}
                    className="w-full h-10 rounded cursor-pointer"
                  />
                </div>
              </div>
            )}
            
            {mode === MODES.CREATIVE && (
              <div className="p-4 space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Brush className="w-5 h-5" /> Creative Studio
                </h3>
                
                <div className="space-y-2">
                  <Label>Color</Label>
                  <input
                    type="color"
                    value={creativeColor}
                    onChange={(e) => setCreativeColor(e.target.value)}
                    className="w-full h-12 rounded cursor-pointer"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Brush Size: {brushSize}px</Label>
                  <Slider
                    value={[brushSize]}
                    onValueChange={([v]) => setBrushSize(v)}
                    min={5}
                    max={100}
                    step={5}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Texture</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {["solid", "gradient", "dotted", "striped"].map(tex => (
                      <Button
                        key={tex}
                        variant={creativeTexture === tex ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCreativeTexture(tex)}
                      >
                        {tex}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <Button className="w-full" onClick={() => {
                  const label = prompt("Enter label text:");
                  if (label) {
                    setCustomLabels(prev => [...prev, { id: Date.now(), text: label, x: 50, y: 50, color: creativeColor }]);
                  }
                }}>
                  <Plus className="w-4 h-4 mr-1" /> Add Label
                </Button>
              </div>
            )}
            
            {/* Get Out and Plant Checklist */}
            <div className="border-t p-4">
              <GetStartedChecklist compact={true} user={user} />
            </div>
            
            {/* Design List */}
            <div className="border-t p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm">My Designs</h4>
                <Button size="sm" variant="outline" onClick={createNewDesign} data-testid="new-design-btn">
                  <Plus className="w-3 h-3 mr-1" /> New
                </Button>
              </div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {designs.map(design => (
                  <div
                    key={design.id}
                    className={`p-2 rounded-lg cursor-pointer flex items-center justify-between group ${currentDesign?.id === design.id ? 'bg-green-100' : 'hover:bg-gray-100'}`}
                    onClick={() => loadDesign(design.id)}
                  >
                    <span className="text-sm truncate flex-1">{design.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        )}

        {/* Main Canvas */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Toolbar - Mobile Optimized */}
          {mode !== MODES.GALLERY && (
            <div className="bg-white border-b p-1.5 sm:p-2">
              {/* Primary toolbar row */}
              <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                {/* Photo upload */}
                <Button variant="outline" size="sm" className="px-2 sm:px-3" onClick={() => fileInputRef.current?.click()} disabled={!currentDesign}>
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1">Photo</span>
                </Button>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                
                {backgroundImage && (
                  <Button variant="outline" size="sm" className="px-2" onClick={() => setBackgroundImage(null)}>
                    <X className="w-4 h-4" />
                    <span className="hidden sm:inline ml-1">BG</span>
                  </Button>
                )}
                
                <div className="h-6 w-px bg-border hidden sm:block" />
                
                {/* Canvas size - collapsed on mobile */}
                <div className="hidden sm:flex items-center gap-1">
                  <Label className="text-xs">W:</Label>
                  <Input type="number" value={canvasWidth} onChange={(e) => setCanvasWidth(Number(e.target.value))} className="w-12 sm:w-14 h-7 sm:h-8 text-sm" disabled={!currentDesign} />
                  <Label className="text-xs">H:</Label>
                  <Input type="number" value={canvasHeight} onChange={(e) => setCanvasHeight(Number(e.target.value))} className="w-12 sm:w-14 h-7 sm:h-8 text-sm" disabled={!currentDesign} />
                  <span className="text-xs text-muted-foreground">ft</span>
                </div>
                
                {mode === MODES.DESIGN && (
                  <Button
                    variant={showPreviousSeason ? "default" : "outline"}
                    size="sm"
                    className="px-2 sm:px-3"
                    onClick={() => setShowPreviousSeason(!showPreviousSeason)}
                    disabled={!currentDesign}
                  >
                    <History className="w-4 h-4" />
                    <span className="hidden sm:inline ml-1">{showPreviousSeason ? "Prev" : "Current"}</span>
                  </Button>
                )}
                
                {/* Emoji Draw Mode Toggle */}
                {mode === MODES.DESIGN && (
                  <Button
                    variant={drawMode ? "default" : "outline"}
                    size="sm"
                    className={`px-2 sm:px-3 ${drawMode ? "bg-purple-600 hover:bg-purple-700" : ""}`}
                    onClick={() => setDrawMode(!drawMode)}
                    disabled={!currentDesign}
                    data-testid="draw-mode-toggle"
                  >
                    <Pencil className="w-4 h-4" />
                    <span className="hidden sm:inline ml-1">{drawMode ? "Drawing" : "Draw"}</span>
                  </Button>
                )}
                
                <div className="flex-1" />
                
                {/* AI Render - compact on mobile */}
                <div className="flex items-center gap-1 sm:gap-2">
                  <select
                    value={renderStyle}
                    onChange={(e) => setRenderStyle(e.target.value)}
                    className="h-7 sm:h-8 px-1.5 sm:px-2 border rounded text-xs sm:text-sm w-20 sm:w-auto"
                  >
                    <option value="realistic">Realistic</option>
                    <option value="watercolor">Watercolor</option>
                    <option value="sketch">Sketch</option>
                    <option value="zen">Zen</option>
                    <option value="vibrant">Vibrant</option>
                  </select>
                  <Button variant="outline" size="sm" className="px-2 sm:px-3" onClick={generateAIRender} disabled={!currentDesign || rendering}>
                    {rendering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                    <span className="hidden sm:inline ml-1">Render</span>
                  </Button>
                </div>
                
                <div className="h-6 w-px bg-border hidden sm:block" />
                
                {/* Living Garden Toggle */}
                <Button
                  variant={isGardenAlive ? "default" : "outline"}
                  size="sm"
                  className={`px-2 sm:px-3 ${isGardenAlive ? "bg-green-600 hover:bg-green-700" : ""}`}
                  onClick={() => setIsGardenAlive(!isGardenAlive)}
                  disabled={!currentDesign || plants.length === 0}
                  data-testid="living-garden-toggle"
                >
                  {isGardenAlive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  <span className="hidden sm:inline ml-1">{isGardenAlive ? "Pause" : "Animate"}</span>
                </Button>
                
                {totalWarnings > 0 && (
                  <Badge variant="destructive" className="ml-1 sm:ml-2">
                    <AlertTriangle className="w-3 h-3" />
                    <span className="ml-1">{totalWarnings}</span>
                  </Badge>
                )}
              </div>
              
              {/* Draw Mode Panel - Mobile Optimized */}
              {drawMode && mode === MODES.DESIGN && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mt-2 pt-2 border-t"
                >
                  {/* Mobile: Horizontal scroll for emojis */}
                  <div className="sm:hidden">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-xs font-medium flex items-center gap-1 flex-shrink-0">
                        <Sticker className="w-3 h-3" />
                        Drawing:
                      </p>
                      <div className="flex items-center gap-1 bg-purple-50 rounded-lg px-2 py-1">
                        {selectedDrawEmoji.isCustom ? (
                          <img src={selectedDrawEmoji.customImage} alt={selectedDrawEmoji.name} className="w-5 h-5 object-contain" />
                        ) : (
                          <span className="text-lg">{selectedDrawEmoji.emoji}</span>
                        )}
                        <span className="text-xs font-medium">{selectedDrawEmoji.name}</span>
                      </div>
                      {drawnPaths.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {drawnPaths.length} paths
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide">
                      {drawingEmojis.map((item, i) => (
                        <button
                          key={i}
                          className={`text-2xl p-2 rounded-lg flex-shrink-0 transition-colors active:scale-95 ${
                            selectedDrawEmoji.emoji === item.emoji && !selectedDrawEmoji.isCustom 
                              ? "bg-purple-100 ring-2 ring-purple-500" 
                              : "bg-gray-100"
                          }`}
                          onClick={() => setSelectedDrawEmoji(item)}
                        >
                          {item.emoji}
                        </button>
                      ))}
                      {customStickers.map((sticker) => (
                        <button
                          key={sticker.id}
                          className={`w-10 h-10 p-1 rounded-lg flex-shrink-0 transition-colors ${
                            selectedDrawEmoji.isCustom && selectedDrawEmoji.customImage === sticker.image
                              ? "bg-purple-100 ring-2 ring-purple-500" 
                              : "bg-gray-100"
                          }`}
                          onClick={() => setSelectedDrawEmoji({ 
                            emoji: "🖼️", 
                            name: sticker.name, 
                            isCustom: true, 
                            customImage: sticker.image,
                            spacing: 4 
                          })}
                        >
                          <img src={sticker.image} alt={sticker.name} className="w-full h-full object-contain rounded" />
                        </button>
                      ))}
                      <button
                        className="w-10 h-10 p-2 rounded-lg border-2 border-dashed border-gray-300 flex-shrink-0 flex items-center justify-center"
                        onClick={() => setShowStickerUpload(true)}
                      >
                        <ImagePlus className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      👆 Drag your finger across the canvas to draw
                    </p>
                  </div>

                  {/* Desktop: Full panel layout */}
                  <div className="hidden sm:flex items-start gap-4 flex-wrap">
                    {/* Emoji Selection */}
                    <div className="flex-1 min-w-[200px]">
                      <p className="text-xs font-medium mb-2 flex items-center gap-1">
                        <Sticker className="w-3 h-3" />
                        Select Emoji to Draw
                      </p>
                      <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto bg-gray-50 p-2 rounded-lg">
                        {drawingEmojis.map((item, i) => (
                          <button
                            key={i}
                            className={`text-xl p-1 rounded hover:bg-gray-200 transition-colors ${
                              selectedDrawEmoji.emoji === item.emoji && !selectedDrawEmoji.isCustom 
                                ? "bg-purple-100 ring-2 ring-purple-500" 
                                : ""
                            }`}
                            onClick={() => setSelectedDrawEmoji(item)}
                            title={item.name}
                          >
                            {item.emoji}
                          </button>
                        ))}
                        {customStickers.map((sticker) => (
                          <button
                            key={sticker.id}
                            className={`w-8 h-8 p-0.5 rounded hover:bg-gray-200 transition-colors ${
                              selectedDrawEmoji.isCustom && selectedDrawEmoji.customImage === sticker.image
                                ? "bg-purple-100 ring-2 ring-purple-500" 
                                : ""
                            }`}
                            onClick={() => setSelectedDrawEmoji({ 
                              emoji: "🖼️", 
                              name: sticker.name, 
                              isCustom: true, 
                              customImage: sticker.image,
                              spacing: 4 
                            })}
                            title={sticker.name}
                          >
                            <img src={sticker.image} alt={sticker.name} className="w-full h-full object-contain rounded" />
                          </button>
                        ))}
                        <button
                          className="w-8 h-8 p-1 rounded border-2 border-dashed border-gray-300 hover:border-purple-500 hover:bg-purple-50 transition-colors flex items-center justify-center"
                          onClick={() => setShowStickerUpload(true)}
                          title="Add custom sticker"
                        >
                          <ImagePlus className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Selected Emoji Info */}
                    <div className="w-40">
                      <p className="text-xs font-medium mb-2">Drawing With:</p>
                      <div className="bg-purple-50 rounded-lg p-2 flex items-center gap-2">
                        {selectedDrawEmoji.isCustom ? (
                          <img src={selectedDrawEmoji.customImage} alt={selectedDrawEmoji.name} className="w-8 h-8 object-contain" />
                        ) : (
                          <span className="text-2xl">{selectedDrawEmoji.emoji}</span>
                        )}
                        <div>
                          <p className="text-sm font-medium">{selectedDrawEmoji.name}</p>
                          <p className="text-xs text-muted-foreground">{selectedDrawEmoji.category || "custom"}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Sign Style Selector */}
                    <div className="w-36">
                      <p className="text-xs font-medium mb-2">Sign Style:</p>
                      <div className="flex flex-wrap gap-1">
                        {signStyles.map(style => (
                          <button
                            key={style.id}
                            onClick={() => setSignStyle(style.id)}
                            className={`text-lg p-1 rounded transition-colors ${
                              signStyle === style.id ? "bg-amber-100 ring-2 ring-amber-500" : "bg-gray-100 hover:bg-gray-200"
                            }`}
                            title={style.name}
                          >
                            {style.emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Drawn Paths Summary */}
                    <div className="w-64">
                      <p className="text-xs font-medium mb-2 flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        Drawn Paths ({drawnPaths.length})
                      </p>
                      <div className="bg-gray-50 rounded-lg p-2 max-h-32 overflow-y-auto">
                        {drawnPaths.length === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            Drag on canvas to draw
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {drawnPaths.slice(-4).map((path) => (
                              <div key={path.id} className="bg-white rounded p-1.5 border">
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className="font-medium">{path.emoji} {path.lengthFeet}ft</span>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => togglePathLabel(path.id)}
                                      className={`p-0.5 rounded ${path.label?.show ? 'bg-amber-100 text-amber-600' : 'hover:bg-gray-100'}`}
                                      title={path.label?.show ? "Hide sign" : "Show sign"}
                                    >
                                      <Tag className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => deleteDrawnPath(path.id)}
                                      className="p-0.5 hover:bg-red-100 rounded"
                                    >
                                      <X className="w-3 h-3 text-red-500" />
                                    </button>
                                  </div>
                                </div>
                                {editingLabelId === path.id ? (
                                  <div className="flex gap-1">
                                    <input
                                      type="text"
                                      value={editingLabelText}
                                      onChange={(e) => setEditingLabelText(e.target.value)}
                                      className="flex-1 text-xs px-1 py-0.5 border rounded"
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          updatePathLabel(path.id, editingLabelText);
                                          setEditingLabelId(null);
                                        } else if (e.key === 'Escape') {
                                          setEditingLabelId(null);
                                        }
                                      }}
                                    />
                                    <button
                                      onClick={() => {
                                        updatePathLabel(path.id, editingLabelText);
                                        setEditingLabelId(null);
                                      }}
                                      className="p-0.5 bg-green-100 rounded hover:bg-green-200"
                                    >
                                      <Check className="w-3 h-3 text-green-600" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => {
                                        setEditingLabelId(path.id);
                                        setEditingLabelText(path.label?.text || path.name);
                                      }}
                                      className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 flex-1"
                                    >
                                      <Type className="w-3 h-3" />
                                      {path.label?.text || path.name}
                                    </button>
                                    <button
                                      onClick={() => startVoiceInput(path.id)}
                                      className={`p-0.5 rounded ${isListening && editingLabelId === path.id ? 'bg-red-100 text-red-600 animate-pulse' : 'hover:bg-gray-100'}`}
                                      title="Voice input"
                                    >
                                      {isListening && editingLabelId === path.id ? (
                                        <MicOff className="w-3 h-3" />
                                      ) : (
                                        <Mic className="w-3 h-3" />
                                      )}
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                            {drawnPaths.length > 4 && (
                              <p className="text-xs text-muted-foreground text-center">+{drawnPaths.length - 4} more...</p>
                            )}
                          </div>
                        )}
                      </div>
                      {drawnPaths.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full mt-1 h-6 text-xs text-red-500 hover:text-red-600"
                          onClick={clearAllPaths}
                        >
                          <Eraser className="w-3 h-3 mr-1" />
                          Clear All
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <p className="hidden sm:block text-xs text-muted-foreground mt-2">
                    💡 Tip: Draw lines of plants on the canvas. When you render, the AI will create realistic {selectedDrawEmoji.name} at the correct scale!
                  </p>
                </motion.div>
              )}
            </div>
          )}

          {/* Canvas or Gallery or Templates */}
          <div className="flex-1 overflow-auto p-2 sm:p-4 bg-gray-100">
            {mode === MODES.TEMPLATES ? (
              <div className="max-w-6xl mx-auto">
                {/* Template Detail Modal */}
                {selectedTemplate && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                    onClick={() => setSelectedTemplate(null)}
                  >
                    <motion.div 
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-6 border-b flex justify-between items-start">
                        <div>
                          <h2 className="text-2xl font-bold">{selectedTemplate.name}</h2>
                          <p className="text-muted-foreground flex items-center gap-1 mt-1">
                            <MapPin className="w-4 h-4" /> {selectedTemplate.location}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedTemplate(null)}>
                          <X className="w-5 h-5" />
                        </Button>
                      </div>
                      
                      <div className="p-6 space-y-6">
                        <p className="text-lg">{selectedTemplate.description}</p>
                        
                        {/* Environmental Insights */}
                        <Card className="bg-gradient-to-br from-green-50 to-blue-50 border-green-200">
                          <CardContent className="p-6">
                            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                              <Info className="w-5 h-5 text-green-600" />
                              Environmental Insights
                            </h3>
                            <p className="text-sm leading-relaxed text-gray-700 mb-4">
                              {selectedTemplate.environmental_insight}
                            </p>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="bg-white/70 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-amber-600 mb-1">
                                  <Thermometer className="w-4 h-4" />
                                  <span className="text-xs font-medium">Summer</span>
                                </div>
                                <span className="text-sm font-bold">{selectedTemplate.avg_temp_summer}</span>
                              </div>
                              <div className="bg-white/70 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-blue-600 mb-1">
                                  <Thermometer className="w-4 h-4" />
                                  <span className="text-xs font-medium">Winter</span>
                                </div>
                                <span className="text-sm font-bold">{selectedTemplate.avg_temp_winter}</span>
                              </div>
                              <div className="bg-white/70 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-blue-500 mb-1">
                                  <Droplets className="w-4 h-4" />
                                  <span className="text-xs font-medium">Rainfall</span>
                                </div>
                                <span className="text-sm font-bold">{selectedTemplate.annual_rainfall}</span>
                              </div>
                              <div className="bg-white/70 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-yellow-600 mb-1">
                                  <Sun className="w-4 h-4" />
                                  <span className="text-xs font-medium">Zone</span>
                                </div>
                                <span className="text-sm font-bold">{selectedTemplate.hardiness_zone}</span>
                              </div>
                            </div>
                            
                            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground">Climate:</span>
                                <span className="ml-2 font-medium">{selectedTemplate.climate_zone}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Soil:</span>
                                <span className="ml-2 font-medium">{selectedTemplate.soil_type}</span>
                              </div>
                              <div className="col-span-2">
                                <span className="text-muted-foreground">Growing Season:</span>
                                <span className="ml-2 font-medium">{selectedTemplate.growing_season}</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        
                        {/* Suggested Plants */}
                        <div>
                          <h3 className="font-semibold text-lg mb-3">Suggested Plants</h3>
                          <div className="flex flex-wrap gap-2">
                            {selectedTemplate.suggested_plants?.map((plant, i) => (
                              <Badge key={i} variant="secondary" className="text-sm py-1 px-3">
                                <Leaf className="w-3 h-3 mr-1" /> {plant}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        
                        {/* Preview */}
                        <div>
                          <h3 className="font-semibold text-lg mb-3">Layout Preview</h3>
                          <div className="bg-green-100 rounded-lg p-4 relative" style={{ minHeight: "200px" }}>
                            {selectedTemplate.plants?.map((plant, i) => (
                              <div 
                                key={i}
                                className="absolute text-2xl"
                                style={{ left: `${plant.x}%`, top: `${plant.y}%`, transform: 'translate(-50%, -50%)' }}
                                title={plant.plant_name}
                              >
                                {plant.icon}
                              </div>
                            ))}
                            {selectedTemplate.structures?.map((struct, i) => (
                              <div 
                                key={i}
                                className="absolute text-2xl"
                                style={{ left: `${struct.x}%`, top: `${struct.y}%`, transform: 'translate(-50%, -50%)' }}
                                title={struct.name}
                              >
                                {struct.icon}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <Button 
                          className="w-full" 
                          size="lg"
                          onClick={() => applyTemplate(selectedTemplate.id)}
                          data-testid="use-template-btn"
                        >
                          <Plus className="w-5 h-5 mr-2" />
                          Start With This Template
                        </Button>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
                
                {/* Header */}
                <div className="mb-6">
                  <h2 className="text-2xl font-bold mb-2">Explore World Gardens</h2>
                  <p className="text-muted-foreground">
                    Discover garden designs inspired by 20 famous locations around the world. 
                    Learn about local climates, soils, and native plants.
                  </p>
                </div>

                {/* Address-Based Template Generator */}
                <Card className="mb-6 bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 border-green-200">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-green-100 hidden sm:block">
                        <Navigation className="w-6 h-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg mb-1 flex items-center gap-2">
                          <Navigation className="w-5 h-5 text-green-600 sm:hidden" />
                          Generate Template for Your Location
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Enter your address or use the map to select your exact location and get a custom 
                          garden template with edible plants that thrive in your growing zone.
                        </p>
                        
                        <div className="flex flex-col gap-3">
                          <div className="flex flex-col sm:flex-row gap-3">
                            <div className="flex-1 relative">
                              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                              <Input
                                placeholder="Enter your address (e.g., 123 Main St, Miami, FL)"
                                value={addressSearch}
                                onChange={(e) => setAddressSearch(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && generateZoneTemplate()}
                                className="pl-9"
                                data-testid="address-search-input"
                              />
                            </div>
                            <Button
                              onClick={generateZoneTemplate}
                              disabled={generatingZoneTemplate || !addressSearch.trim()}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid="generate-zone-template-btn"
                            >
                              {generatingZoneTemplate ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Search className="w-4 h-4 mr-2" />
                              )}
                              Find My Zone
                            </Button>
                          </div>
                          
                          {/* Map Button */}
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              onClick={() => setShowLocationMap(true)}
                              className="flex-1 border-green-300 text-green-700 hover:bg-green-50"
                              data-testid="open-location-map-btn"
                            >
                              <Map className="w-4 h-4 mr-2" />
                              Pick on Map
                            </Button>
                            <Button
                              variant="outline"
                              onClick={useCurrentLocation}
                              className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                              data-testid="use-current-location-btn"
                            >
                              <Navigation className="w-4 h-4 mr-2" />
                              Use My Location
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Location Map Modal */}
                <AnimatePresence>
                  {showLocationMap && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
                      onClick={() => setShowLocationMap(false)}
                    >
                      <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-4 border-b bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-bold text-lg">Select Your Garden Location</h3>
                              <p className="text-sm text-green-100">Click anywhere on the map to generate a zone-specific template</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Satellite Toggle */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setMapSatelliteView(!mapSatelliteView)}
                                className={`text-white hover:bg-white/20 ${mapSatelliteView ? 'bg-white/20' : ''}`}
                                data-testid="satellite-toggle-btn"
                              >
                                {mapSatelliteView ? (
                                  <>
                                    <Map className="w-4 h-4 mr-1" />
                                    Street
                                  </>
                                ) : (
                                  <>
                                    <Globe className="w-4 h-4 mr-1" />
                                    Satellite
                                  </>
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowLocationMap(false)}
                                className="text-white hover:bg-white/20"
                              >
                                <X className="w-5 h-5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                        
                        <div className="h-[500px] relative">
                          <MapContainer
                            center={[mapPosition.lat, mapPosition.lng]}
                            zoom={10}
                            style={{ height: "100%", width: "100%" }}
                            data-testid="location-picker-map"
                          >
                            {mapSatelliteView ? (
                              <TileLayer
                                attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
                                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                              />
                            ) : (
                              <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                              />
                            )}
                            <MapClickHandler />
                            {selectedLocation && (
                              <Marker position={[selectedLocation.lat, selectedLocation.lng]} />
                            )}
                          </MapContainer>
                          
                          {/* Map type indicator */}
                          <div className="absolute top-3 left-3 z-[1000]">
                            <Badge className={mapSatelliteView ? "bg-blue-600" : "bg-green-600"}>
                              {mapSatelliteView ? "🛰️ Satellite View" : "🗺️ Street Map"}
                            </Badge>
                          </div>
                          
                          {generatingZoneTemplate && (
                            <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-[1000]">
                              <div className="text-center">
                                <Loader2 className="w-8 h-8 animate-spin text-green-600 mx-auto mb-2" />
                                <p className="text-gray-600">Analyzing location...</p>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="p-4 bg-gray-50 border-t flex items-center justify-between">
                          <div className="text-sm text-gray-500">
                            {selectedLocation ? (
                              <span className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-green-600" />
                                {selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)}
                              </span>
                            ) : (
                              "Click on the map to select a location"
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setMapSatelliteView(!mapSatelliteView)}
                              className="border-gray-300"
                            >
                              {mapSatelliteView ? <Map className="w-4 h-4 mr-1" /> : <Globe className="w-4 h-4 mr-1" />}
                              {mapSatelliteView ? "Street" : "Satellite"}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={useCurrentLocation}
                            >
                              <Navigation className="w-4 h-4 mr-1" />
                              My Location
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Generated Zone Template Modal */}
                {generatedZoneTemplate && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                    onClick={() => setGeneratedZoneTemplate(null)}
                  >
                    <motion.div 
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-6 border-b bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-t-2xl">
                        <div className="flex justify-between items-start">
                          <div>
                            <Badge className="bg-white/20 text-white border-0 mb-2">
                              Zone {generatedZoneTemplate.hardiness_zone}
                            </Badge>
                            <h2 className="text-2xl font-bold">{generatedZoneTemplate.name}</h2>
                            <p className="text-white/90 flex items-center gap-1 mt-1">
                              <MapPin className="w-4 h-4" /> {addressSearch}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={() => setGeneratedZoneTemplate(null)}>
                            <X className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="p-6 space-y-6">
                        <p className="text-lg">{generatedZoneTemplate.description}</p>
                        
                        {/* Environmental Info */}
                        <Card className="bg-gradient-to-br from-green-50 to-blue-50 border-green-200">
                          <CardContent className="p-6">
                            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                              <Info className="w-5 h-5 text-green-600" />
                              Your Growing Conditions
                            </h3>
                            <p className="text-sm leading-relaxed text-gray-700 mb-4">
                              {generatedZoneTemplate.environmental_insight}
                            </p>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="bg-white/70 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-amber-600 mb-1">
                                  <Thermometer className="w-4 h-4" />
                                  <span className="text-xs font-medium">Summer</span>
                                </div>
                                <span className="text-sm font-bold">{generatedZoneTemplate.avg_temp_summer}</span>
                              </div>
                              <div className="bg-white/70 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-blue-600 mb-1">
                                  <Thermometer className="w-4 h-4" />
                                  <span className="text-xs font-medium">Winter</span>
                                </div>
                                <span className="text-sm font-bold">{generatedZoneTemplate.avg_temp_winter}</span>
                              </div>
                              <div className="bg-white/70 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-blue-500 mb-1">
                                  <Droplets className="w-4 h-4" />
                                  <span className="text-xs font-medium">Rainfall</span>
                                </div>
                                <span className="text-sm font-bold">{generatedZoneTemplate.annual_rainfall}</span>
                              </div>
                              <div className="bg-white/70 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-yellow-600 mb-1">
                                  <Sun className="w-4 h-4" />
                                  <span className="text-xs font-medium">Zone</span>
                                </div>
                                <span className="text-sm font-bold">{generatedZoneTemplate.hardiness_zone}</span>
                              </div>
                            </div>
                            
                            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground">Climate:</span>
                                <span className="ml-2 font-medium">{generatedZoneTemplate.climate_zone}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Frost Dates:</span>
                                <span className="ml-2 font-medium">{generatedZoneTemplate.frost_dates}</span>
                              </div>
                              <div className="col-span-2">
                                <span className="text-muted-foreground">Growing Season:</span>
                                <span className="ml-2 font-medium">{generatedZoneTemplate.growing_season}</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        
                        {/* Edible Plants for This Zone */}
                        <div>
                          <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                            <Apple className="w-5 h-5 text-red-500" />
                            Edible Plants for Zone {generatedZoneTemplate.hardiness_zone}
                          </h3>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {generatedZoneTemplate.edible_plants?.map((plant, i) => (
                              <div key={i} className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
                                <span className="text-xl">{plant.emoji || "🌱"}</span>
                                <div>
                                  <p className="font-medium text-sm">{plant.name}</p>
                                  <p className="text-xs text-muted-foreground">{plant.type}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Additional Suggested Plants */}
                        <div>
                          <h3 className="font-semibold text-lg mb-3">All Suggested Plants</h3>
                          <div className="flex flex-wrap gap-2">
                            {generatedZoneTemplate.suggested_plants?.map((plant, i) => (
                              <Badge key={i} variant="secondary" className="text-sm py-1 px-3">
                                <Leaf className="w-3 h-3 mr-1" /> {plant}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        
                        {/* Layout Preview */}
                        {generatedZoneTemplate.plants?.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-lg mb-3">Suggested Layout</h3>
                            <div className="bg-green-100 rounded-lg p-4 relative" style={{ minHeight: "200px" }}>
                              {generatedZoneTemplate.plants.map((plant, i) => (
                                <div 
                                  key={i}
                                  className="absolute text-2xl"
                                  style={{ left: `${plant.x}%`, top: `${plant.y}%`, transform: "translate(-50%, -50%)" }}
                                  title={plant.plant_name}
                                >
                                  {plant.emoji || "🌱"}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setGeneratedZoneTemplate(null)}>
                          Close
                        </Button>
                        <Button 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={useGeneratedTemplate}
                          data-testid="use-zone-template-btn"
                        >
                          <Plus className="w-5 h-5 mr-2" />
                          Start With This Template
                        </Button>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
                
                {/* Continent Filter */}
                <div className="flex flex-wrap gap-2 mb-6">
                  <Button 
                    variant={selectedContinent === null ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedContinent(null)}
                    data-testid="filter-all"
                  >
                    <Globe className="w-4 h-4 mr-1" /> All Regions
                  </Button>
                  {["Europe", "Asia", "North America", "South America", "Africa", "Oceania"].map(cont => (
                    <Button
                      key={cont}
                      variant={selectedContinent === cont ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedContinent(cont)}
                      data-testid={`filter-${cont.toLowerCase().replace(' ', '-')}`}
                    >
                      {cont}
                    </Button>
                  ))}
                </div>
                
                {/* Templates Grid */}
                {loadingTemplates ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {worldTemplates.map(template => (
                      <Card 
                        key={template.id} 
                        className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                        onClick={() => viewTemplateDetails(template.id)}
                        data-testid={`template-${template.id}`}
                      >
                        <div className="h-32 bg-gradient-to-br from-green-200 to-blue-200 relative overflow-hidden">
                          <div className="absolute inset-0 flex items-center justify-center text-5xl opacity-50">
                            {template.continent === "Asia" && "🏯"}
                            {template.continent === "Europe" && "🏰"}
                            {template.continent === "Africa" && "🌍"}
                            {template.continent === "North America" && "🗽"}
                            {template.continent === "South America" && "🏔️"}
                            {template.continent === "Oceania" && "🦘"}
                          </div>
                          <Badge className="absolute top-2 right-2 bg-white/90 text-gray-700">
                            {template.plant_count} plants
                          </Badge>
                        </div>
                        <CardContent className="p-4">
                          <h3 className="font-semibold truncate group-hover:text-primary transition-colors">
                            {template.name}
                          </h3>
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                            <MapPin className="w-3 h-3" /> {template.location}
                          </p>
                          <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                            {template.description}
                          </p>
                          <div className="flex items-center justify-between">
                            <Badge variant="outline" className="text-xs">
                              Zone {template.hardiness_zone}
                            </Badge>
                            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
                
                {worldTemplates.length === 0 && !loadingTemplates && (
                  <div className="text-center py-12">
                    <Globe className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-semibold">No templates found</h3>
                    <p className="text-muted-foreground">Try selecting a different region</p>
                  </div>
                )}
              </div>
            ) : mode === MODES.GALLERY ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold">Community Gallery</h2>
                  <select
                    value={gallerySort}
                    onChange={(e) => setGallerySort(e.target.value)}
                    className="p-2 border rounded-lg"
                  >
                    <option value="recent">Most Recent</option>
                    <option value="popular">Most Popular</option>
                    <option value="remixed">Most Remixed</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {galleryDesigns.map(design => (
                    <Card key={design.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                      <div className="h-40 bg-green-100 relative">
                        {design.background_image ? (
                          <img src={design.background_image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="flex items-center justify-center h-full text-4xl">🌱</div>
                        )}
                        <div className="absolute bottom-2 right-2 flex gap-1">
                          <Badge variant="secondary">{design.plants?.length || 0} plants</Badge>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-semibold truncate">{design.name}</h3>
                        <p className="text-sm text-muted-foreground">by {design.creator_name}</p>
                        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> {design.likes || 0}</span>
                          <span className="flex items-center gap-1"><RefreshCw className="w-4 h-4" /> {design.remix_count || 0}</span>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" variant="outline" onClick={() => likeDesign(design.id)}>
                            <Heart className="w-4 h-4" />
                          </Button>
                          <Button size="sm" onClick={() => remixDesign(design.id)}>
                            <RefreshCw className="w-4 h-4 mr-1" /> Remix
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setMode(MODES.ZEN)}>
                            <Palette className="w-4 h-4 mr-1" /> Color
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {galleryDesigns.length === 0 && (
                    <div className="col-span-full text-center py-12">
                      <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg font-semibold">No designs yet</h3>
                      <p className="text-muted-foreground mb-4">Be the first to share your garden!</p>
                      <Button onClick={() => setMode(MODES.DESIGN)}>Create & Share</Button>
                    </div>
                  )}
                </div>
              </div>
            ) : !currentDesign ? (
              <div className="h-full flex items-center justify-center">
                <Card className="max-w-lg">
                  <CardContent className="p-8 text-center">
                    <Image className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-semibold mb-2">Create Your Garden Design</h3>
                    <p className="text-muted-foreground mb-6">
                      Start from scratch or explore templates from famous gardens around the world.
                    </p>
                    <div className="flex flex-col gap-3">
                      <Button onClick={createNewDesign} data-testid="create-first-design-btn">
                        <Plus className="w-4 h-4 mr-2" /> Start Blank Design
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setMode(MODES.TEMPLATES)}
                        className="border-green-300 text-green-700 hover:bg-green-50"
                        data-testid="explore-templates-btn"
                      >
                        <Globe className="w-4 h-4 mr-2" /> Explore World Gardens
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="relative w-full flex justify-center">
                <div
                  ref={canvasRef}
                  className={`relative bg-white rounded-lg shadow-lg overflow-hidden touch-none w-full max-w-[800px] ${
                    drawMode ? "cursor-crosshair" : "cursor-crosshair"
                  }`}
                  style={{ 
                    aspectRatio: `${canvasWidth} / ${canvasHeight}`,
                    maxHeight: 'calc(100vh - 180px)'
                  }}
                  onClick={drawMode ? undefined : handleCanvasClick}
                  onMouseDown={drawMode ? handleDrawStart : undefined}
                  onMouseMove={drawMode ? handleDrawMove : handleCanvasMouseMove}
                  onMouseUp={drawMode ? handleDrawEnd : () => setDraggingItem(null)}
                  onMouseLeave={drawMode ? handleDrawEnd : () => setDraggingItem(null)}
                  onTouchStart={drawMode ? handleDrawStart : undefined}
                  onTouchMove={drawMode ? handleDrawMove : handleCanvasTouchMove}
                  onTouchEnd={drawMode ? handleDrawEnd : () => setDraggingItem(null)}
                  data-testid="design-canvas"
                >
                  {backgroundImage && (
                    <img src={backgroundImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
                  )}
                  
                  {/* Grid */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: "linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)",
                      backgroundSize: `${100 / canvasWidth}% ${100 / canvasHeight}%`
                    }}
                  />
                  
                  {/* Drawn Emoji Paths */}
                  {drawnPaths.map(path => (
                    <div key={path.id} className="absolute inset-0 pointer-events-none">
                      {path.points.map((point, i) => (
                        <div
                          key={`${path.id}-${i}`}
                          className="absolute"
                          style={{ 
                            left: `${point.x}%`, 
                            top: `${point.y}%`, 
                            transform: "translate(-50%, -50%)",
                            fontSize: "1.5rem"
                          }}
                        >
                          {path.isCustom && path.customImage ? (
                            <img src={path.customImage} alt="" className="w-6 h-6 object-contain" />
                          ) : (
                            point.emoji
                          )}
                        </div>
                      ))}
                      
                      {/* Wooden Sign Label */}
                      {path.label?.show && (
                        <div
                          className="absolute pointer-events-auto cursor-move"
                          style={{
                            left: `${path.label.position?.x || path.points[0]?.x || 10}%`,
                            top: `${path.label.position?.y || Math.max(5, (path.points[0]?.y || 20) - 8)}%`,
                            transform: "translate(-50%, -50%)",
                            zIndex: 20
                          }}
                        >
                          {/* Wooden Sign Post */}
                          <div className="relative">
                            {/* Sign Board */}
                            <div 
                              className="relative px-3 py-1.5 rounded-lg shadow-md border-2"
                              style={{
                                background: "linear-gradient(180deg, #D4A574 0%, #C4956A 50%, #B8845A 100%)",
                                borderColor: "#8B6914",
                                minWidth: "60px",
                                textAlign: "center"
                              }}
                            >
                              {/* Wood grain lines */}
                              <div className="absolute inset-0 opacity-20 rounded-lg overflow-hidden">
                                <div className="absolute top-1 left-0 right-0 h-px bg-amber-900"></div>
                                <div className="absolute top-3 left-0 right-0 h-px bg-amber-900"></div>
                                <div className="absolute bottom-2 left-0 right-0 h-px bg-amber-900"></div>
                              </div>
                              {/* Sign Text */}
                              <span 
                                className="relative font-bold text-xs whitespace-nowrap"
                                style={{ 
                                  color: "#4A3520",
                                  textShadow: "0 1px 0 rgba(255,255,255,0.3)"
                                }}
                              >
                                {path.label.text || path.name}
                              </span>
                              {/* Nail holes */}
                              <div className="absolute top-1 left-1 w-1 h-1 rounded-full bg-amber-900/40"></div>
                              <div className="absolute top-1 right-1 w-1 h-1 rounded-full bg-amber-900/40"></div>
                            </div>
                            {/* Sign Post Stake */}
                            <div 
                              className="mx-auto w-2 h-4 rounded-b"
                              style={{
                                background: "linear-gradient(90deg, #A67C52 0%, #8B6914 50%, #A67C52 100%)",
                              }}
                            ></div>
                            {/* Post into ground */}
                            <div 
                              className="mx-auto w-3 h-1 rounded-full opacity-30"
                              style={{ background: "#4A3520" }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Current Drawing Path (while drawing) */}
                  {isDrawing && currentPath.length > 0 && (
                    <div className="absolute inset-0 pointer-events-none">
                      {currentPath.map((point, i) => (
                        <div
                          key={`current-${i}`}
                          className="absolute opacity-70"
                          style={{ 
                            left: `${point.x}%`, 
                            top: `${point.y}%`, 
                            transform: "translate(-50%, -50%)",
                            fontSize: "1.5rem"
                          }}
                        >
                          {selectedDrawEmoji.isCustom && selectedDrawEmoji.customImage ? (
                            <img src={selectedDrawEmoji.customImage} alt="" className="w-6 h-6 object-contain" />
                          ) : (
                            point.emoji
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Structures */}
                  {structures.map(struct => {
                    const fillColor = mode === MODES.ZEN ? (zenColors[struct.id] || struct.color + "40") : struct.color + "40";
                    return (
                      <motion.div
                        key={struct.id}
                        data-item-id={struct.id}
                        className={`absolute cursor-move select-none ${selectedItem === struct.id ? 'ring-2 ring-primary' : ''}`}
                        style={{
                          left: `${struct.x}%`, top: `${struct.y}%`,
                          transform: "translate(-50%, -50%)",
                          touchAction: "none",
                          backgroundColor: fillColor,
                          width: `${struct.width * 3}%`,
                          height: `${struct.height * 3}%`,
                          borderRadius: struct.category === "shape" && struct.name.includes("Circle") ? "50%" : "8px",
                          border: "2px dashed " + struct.color
                        }}
                        onMouseDown={(e) => { e.stopPropagation(); setDraggingItem(struct.id); setSelectedItem(struct.id); }}
                        onTouchStart={(e) => { e.stopPropagation(); setDraggingItem(struct.id); setSelectedItem(struct.id); }}
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-2xl">{struct.icon}</span>
                        </div>
                        <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs font-medium bg-white/90 px-1 rounded whitespace-nowrap">
                          {struct.name}
                        </span>
                        {selectedItem === struct.id && (
                          <button onClick={() => removeItem(struct.id, "structure")} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1">
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </motion.div>
                    );
                  })}
                  
                  {/* Plants */}
                  {(showPreviousSeason ? previousSeasonPlants : plants).map(plant => {
                    const fillColor = mode === MODES.ZEN ? (zenColors[plant.id] || plant.color + "40") : plant.color + "40";
                    const hasWarning = spacingWarnings.some(w => w.plant1_id === plant.id || w.plant2_id === plant.id);
                    return (
                      <motion.div
                        key={plant.id}
                        data-item-id={plant.id}
                        initial={plant.justPlaced ? { scale: 0, opacity: 0 } : false}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                        className={`absolute cursor-move select-none z-10 group ${selectedItem === plant.id ? 'z-20' : ''}`}
                        style={{ left: `${plant.x}%`, top: `${plant.y}%`, transform: "translate(-50%, -50%)", touchAction: "none" }}
                        onMouseDown={(e) => { e.stopPropagation(); setDraggingItem(plant.id); setSelectedItem(plant.id); }}
                        onTouchStart={(e) => { e.stopPropagation(); setDraggingItem(plant.id); setSelectedItem(plant.id); }}
                      >
                        <div
                          className={`rounded-full p-3 shadow-lg transition-all ${selectedItem === plant.id ? 'ring-4 ring-primary ring-offset-2' : ''} ${hasWarning ? 'ring-2 ring-amber-500' : ''}`}
                          style={{ backgroundColor: fillColor, transform: `scale(${plantScale})` }}
                        >
                          <span style={{ fontSize: `${1.5 * plantScale}rem` }}>{plant.icon}</span>
                        </div>
                        <span className="absolute top-full mt-1 left-1/2 -translate-x-1/2 text-xs font-medium bg-white/90 px-1 rounded whitespace-nowrap">
                          {plant.plant_name}
                        </span>
                        
                        {/* Hover Tooltip - Planting & Harvest Times */}
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                          <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl whitespace-nowrap">
                            <p className="font-semibold mb-1">{plant.plant_name}</p>
                            {plant.days_to_harvest && (
                              <p className="text-gray-300">⏱️ {plant.days_to_harvest} days to harvest</p>
                            )}
                            {plant.planting_months && plant.planting_months.length > 0 && (
                              <p className="text-green-300">🌱 Plant: {plant.planting_months.map(m => ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m-1]).join(', ')}</p>
                            )}
                            {plant.harvest_months && plant.harvest_months.length > 0 && (
                              <p className="text-amber-300">🥬 Harvest: {plant.harvest_months.map(m => ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m-1]).join(', ')}</p>
                            )}
                            {plant.sun_requirement && (
                              <p className="text-yellow-300">{plant.sun_requirement === 'full_sun' ? '☀️ Full Sun' : plant.sun_requirement === 'partial_shade' ? '⛅ Partial Shade' : '🌥️ Shade'}</p>
                            )}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                        
                        {selectedItem === plant.id && (
                          <button onClick={() => removeItem(plant.id, "plant")} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 z-30">
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </motion.div>
                    );
                  })}
                  
                  {/* Placement Puff Animation */}
                  <AnimatePresence>
                    {placementAnimation && (
                      <motion.div
                        initial={{ scale: 0.5, opacity: 1 }}
                        animate={{ scale: 2.5, opacity: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        className="absolute pointer-events-none z-50"
                        style={{ left: `${placementAnimation.x}%`, top: `${placementAnimation.y}%`, transform: "translate(-50%, -50%)" }}
                      >
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-300/60 via-amber-200/40 to-transparent blur-sm" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  {/* Custom Labels (Creative Mode) */}
                  {customLabels.map(label => (
                    <div
                      key={label.id}
                      className="absolute cursor-move"
                      style={{ left: `${label.x}%`, top: `${label.y}%`, transform: "translate(-50%, -50%)", color: label.color }}
                    >
                      <span className="font-bold text-lg drop-shadow-lg">{label.text}</span>
                    </div>
                  ))}
                  
                  {/* Mode indicator and Controls */}
                  <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
                    <div className="bg-white/90 px-1.5 sm:px-2 py-1 rounded text-xs font-medium">
                      {mode === MODES.ZEN && "🧘 Zen"}
                      {mode === MODES.CREATIVE && "🎨 Creative"}
                      {mode === MODES.DESIGN && (showPreviousSeason ? "📅 Previous" : "🌱 Current")}
                      <span className="hidden sm:inline ml-2 text-muted-foreground">
                        ({plants.length} plants, {structures.length} items)
                      </span>
                    </div>
                    <div className="bg-white/90 px-1.5 sm:px-2 py-1 rounded flex items-center gap-1 sm:gap-2">
                      <button 
                        onClick={() => setPlantScale(Math.max(0.5, plantScale - 0.25))}
                        className="p-1 hover:bg-gray-200 rounded"
                        title="Smaller plants"
                      >
                        <ZoomOut className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                      <span className="text-xs font-medium w-8 sm:w-12 text-center">{Math.round(plantScale * 100)}%</span>
                      <button 
                        onClick={() => setPlantScale(Math.min(3, plantScale + 0.25))}
                        className="p-1 hover:bg-gray-200 rounded"
                        title="Larger plants"
                      >
                        <ZoomIn className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Living Garden Overlay - Animated insects and growth effects */}
                  <LivingGardenOverlay
                    plants={plants}
                    structures={structures}
                    isActive={isGardenAlive}
                    canvasWidth={canvasWidth * 35}
                    canvasHeight={canvasHeight * 35}
                  />
                </div>
                
                {/* Mobile FAB - Add Plants/Structures (when not in draw mode) */}
                {currentDesign && mode === MODES.DESIGN && !drawMode && (
                  <Button
                    className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg md:hidden z-30 bg-green-600 hover:bg-green-700"
                    onClick={() => setSidebarOpen(true)}
                    data-testid="mobile-add-fab"
                  >
                    <Plus className="w-6 h-6" />
                  </Button>
                )}
                
                {/* Mobile Draw Mode Floating Panel */}
                {currentDesign && mode === MODES.DESIGN && drawMode && drawnPaths.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="fixed bottom-4 left-4 right-4 md:hidden bg-white rounded-xl shadow-lg border p-3 z-30"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{drawnPaths.length} drawn paths</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-red-500"
                        onClick={clearAllPaths}
                      >
                        <Eraser className="w-3 h-3 mr-1" />
                        Clear
                      </Button>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {drawnPaths.slice(-5).map((path) => (
                        <div key={path.id} className="flex-shrink-0 bg-gray-50 rounded-lg px-2 py-1 flex items-center gap-1">
                          <span>{path.emoji}</span>
                          <span className="text-xs">{path.lengthFeet}ft</span>
                          <button
                            onClick={() => togglePathLabel(path.id)}
                            className={`p-0.5 rounded ${path.label?.show ? 'text-amber-600' : 'text-gray-400'}`}
                          >
                            <Tag className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => deleteDrawnPath(path.id)}
                            className="p-0.5 text-red-400"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
                
                {/* AI Render Preview */}
                {renderImage && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 bg-white rounded-lg shadow-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-yellow-500" /> 
                        {editedRenderImage ? "Edited Garden Render" : "AI Garden Render"}
                      </h3>
                      <div className="flex gap-2">
                        {/* Edit Render Button - Opens Paint Editor */}
                        <Button 
                          size="sm" 
                          className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                          onClick={() => setShowPaintEditor(true)}
                          data-testid="edit-render-btn"
                        >
                          <Paintbrush className="w-4 h-4 mr-1" /> Edit Render
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => window.open(editedRenderImage || renderImage, "_blank")}>
                          <Eye className="w-4 h-4 mr-1" /> View Full
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => {
                          const a = document.createElement("a");
                          a.href = editedRenderImage || renderImage;
                          a.download = `${designName}-render.png`;
                          a.click();
                        }}>
                          <Download className="w-4 h-4 mr-1" /> Download
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => {
                          setRenderImage(null);
                          setEditedRenderImage(null);
                        }}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Toggle between Original and Edited */}
                    {editedRenderImage && (
                      <div className="flex gap-2 mb-3">
                        <Button
                          size="sm"
                          variant={!editedRenderImage ? "default" : "outline"}
                          onClick={() => setEditedRenderImage(null)}
                          className="text-xs"
                        >
                          Original
                        </Button>
                        <Button
                          size="sm"
                          variant={editedRenderImage ? "default" : "outline"}
                          className="text-xs bg-green-600 hover:bg-green-700"
                        >
                          Edited Version
                        </Button>
                      </div>
                    )}
                    
                    <img 
                      src={editedRenderImage || renderImage} 
                      alt="AI Render" 
                      className="w-full max-h-96 object-contain rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setShowPaintEditor(true)}
                      title="Click to edit"
                    />
                    
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Click image or &quot;Edit Render&quot; to paint, add plants, or highlight areas
                    </p>
                  </motion.div>
                )}
              </div>
            )}
          </div>
        </main>

        {/* Paint Editor Modal */}
        <AnimatePresence>
          {showPaintEditor && renderImage && (
            <RenderPaintEditor
              imageUrl={editedRenderImage || renderImage}
              designName={designName}
              onSave={(editedDataUrl) => {
                setEditedRenderImage(editedDataUrl);
                setShowPaintEditor(false);
                toast.success("Render edited successfully!");
              }}
              onClose={() => setShowPaintEditor(false)}
            />
          )}
        </AnimatePresence>

        {/* Warnings Panel (Design Mode) */}
        {mode === MODES.DESIGN && currentDesign && totalWarnings > 0 && (
          <aside className="w-64 bg-white border-l p-4 overflow-y-auto">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Warnings
            </h3>
            {spacingWarnings.map((w, i) => (
              <Card key={i} className="mb-2 border-amber-200 bg-amber-50">
                <CardContent className="p-3">
                  <p className="text-sm font-medium text-amber-800">{w.plant1_name} & {w.plant2_name}</p>
                  <p className="text-xs text-amber-700">{w.message}</p>
                </CardContent>
              </Card>
            ))}
          </aside>
        )}
      </div>
      
      {/* Achievement Celebration Modal */}
      <AnimatePresence>
        {newAchievement && (
          <AchievementCelebration 
            achievement={newAchievement}
            onComplete={() => setNewAchievement(null)}
          />
        )}
      </AnimatePresence>
      
      {/* Sticker Upload Modal */}
      <AnimatePresence>
        {showStickerUpload && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowStickerUpload(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <ImagePlus className="w-5 h-5 text-purple-500" />
                  Add Custom Sticker
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setShowStickerUpload(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                Upload your own image to use as a drawing sticker. PNG with transparency works best!
              </p>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-500 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleStickerUpload}
                  className="hidden"
                  id="sticker-upload"
                />
                <label htmlFor="sticker-upload" className="cursor-pointer">
                  <ImagePlus className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium">Click to upload image</p>
                  <p className="text-xs text-muted-foreground">PNG, JPG, or SVG</p>
                </label>
              </div>
              
              {customStickers.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">Your Stickers ({customStickers.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {customStickers.map(sticker => (
                      <div key={sticker.id} className="relative group">
                        <img src={sticker.image} alt={sticker.name} className="w-12 h-12 object-contain border rounded" />
                        <button
                          onClick={() => deleteCustomSticker(sticker.id)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VisualDesignTool;
